/*
 * Connect client — the data layer's single transport to api-gateway.
 *
 * LIBRARY-GRADE BOUNDARY (the rule that keeps this extractable to @vitesse/core):
 * this module depends ONLY on @connectrpc/* + @vitesse/proto + env. No React,
 * no Lit, no DOM-component import ever leaks in here. Auth is a token CALLBACK,
 * not the IdP SDK — the host app wires the token source to it (workers have no DOM; the SDK is
 * main-thread). That callback boundary is identical whether this lives in the
 * frontend or a published package.
 */
import { Code, ConnectError, createClient, type Client, type Interceptor, type Transport } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';
import { SessionEntitlements } from '@vitesse/proto/vitesse/v1/entitlements_pb.js';
import { Controllers, ControllerEvent_Op, type ControllerDescriptor } from '@vitesse/proto/vitesse/v1/controller_pb.js';
import {
  Integrations,
  IntegrationEvent_Op,
  RowPredicate_Op,
  ViewKind,
  type IntegrationDescriptor,
  type IntegrationRuntime,
  type IntegrationType,
  type Row,
  type Value,
  NamedTarget_Mode,
} from '@vitesse/proto/vitesse/v1/integrations_pb.js';
import type { Integration, IntegrationForward, IntegrationOp, IntegrationViewKind } from '../domain/integration';
import { SymbolIndex, IntegrationIndex, type SymbolPage } from '@vitesse/proto/vitesse/v1/symbol_index_pb.js';
import { ProgramIndex, ProgramBlockKind } from '@vitesse/proto/vitesse/v1/program_index_pb.js';
import { Values, ValueEvent_Op } from '@vitesse/proto/vitesse/v1/values_pb.js';
import { Plugins, PluginEvent_Op } from '@vitesse/proto/vitesse/v1/command_pb.js';
import { type PluginDescriptor } from '@vitesse/proto/vitesse/v1/plugin_pb.js';
import { FieldComponent } from '@vitesse/proto/vitesse/v1/form_pb.js';
import { Historian, AggKind } from '@vitesse/proto/vitesse/v1/historian_pb.js';
import { resultFailed, resultErrorText, describeRefusal } from './result.js';
import { inFlightLimit, mergeByTime, isRoutineHistorianSummary, type TypedSample, type TypedHistory } from './history-typed.js';
export type { TypedSample, TypedHistory } from './history-typed.js';
import { permitPool, walkColumns, type HistoryColumns } from './history-columns.js';
export type { HistoryColumns } from './history-columns.js';
/*
 * The pure half lives in its own module so it can be imported without a
 * browser — this one cannot be (env.ts resolves a deploy target and a document
 * origin at import). Re-exported so callers keep one import site.
 */
import { tileFromWire, boardToWire, backgroundFromWire, type DashboardBodyWire } from './dashboard-wire.js';
import type { DashboardTile } from '../domain/dashboard';
export {
  tileToWire,
  tileFromWire,
  boardToWire,
  applyDashboardEvent,
  emptyRoster,
  type RosterState,
  type DashboardBodyWire,
} from './dashboard-wire.js';
import { UserData, LabelEvent_Op, ControllerDescriptionEvent_Op, GatewayDescriptionEvent_Op } from '@vitesse/proto/vitesse/v1/user_data_pb.js';
import {
  Models,
  ModelEvent_Op,
  type ModelBinding,
  type ModelBindingSchema,
  type ModelChildSchema,
  type ModelDescriptor,
  type ModelNotifySchema,
  type ModelPlcOutput,
  type ModelPlcOutputSchema,
  type ModelRuntime,
  type ModelTemplateSchema,
} from '@vitesse/proto/vitesse/v1/model_pb.js';
import type { MessageInitShape } from '@bufbuild/protobuf';
import type { SymbolValue } from '@vitesse/proto/vitesse/v1/symbol_pb.js';
import { FleetControl, GatewayStatus, type GatewayRecord } from '@vitesse/proto/vitesse/v1/fleet_control_pb.js';
import { Video, CameraEvent_Op, type CameraDescriptor } from '@vitesse/proto/vitesse/v1/video_pb.js';
import type { ModelView, ModelStatus, ModelIface, ModelChildView, ModelChildPort } from '../domain/model';
import { API_GATEWAY_URL, CLOUD_API_GATEWAY_URL } from '../env';
import { decodeSymbolPath, encodeSymbolPath } from './pathenc';
import type { Controller, ConnectionStatus, DiscoveryStatus } from '../domain/controller';
import type { Camera, CameraStatus } from '../domain/camera';
import { Dashboards, ShareAudience } from '@vitesse/proto/vitesse/v1/dashboard_pb.js';
import type { DashboardSummary } from '../domain/dashboard';

/** Mutable token holder — the host updates `.token`; the interceptor reads it.
 *  `refresh` (host-supplied callback) fetches a FRESH token on demand — the
 *  recovery path when the pushed token went stale (background-tab interval
 *  throttling, laptop sleep) and a request comes back `unauthenticated`. */
export interface TokenHolder {
  token: string | null;
  refresh?: () => Promise<string | null>;
}

/**
 * Sets the ONE header the api-gateway requires on every authenticated request:
 *   - Authorization: Bearer <jwt>      (IdP session online / LST offline)
 *
 * The client is STATELESS about gateway scope — it never sends a gateway id. The
 * server RESOLVES the scope: edge injects the box's own device id; cloud resolves
 * the JWT-org's owned gateway from the registry. (`X-Vitesse-Gateway-Id` survives
 * server-side as an OPTIONAL membership-validated override for future multi-
 * gateway selection; the agent app doesn't send it.)
 */
function headerInterceptor(holder: TokenHolder): Interceptor {
  return (next) => async (req) => {
    if (holder.token) req.header.set('Authorization', `Bearer ${holder.token}`);
    return next(req);
  };
}

/** On `unauthenticated`, pull a FRESH token via holder.refresh and retry the
 *  request ONCE. Unary only — a server stream can't be retried mid-flight by an
 *  interceptor, so the worker's supervise() loop does the equivalent: on an
 *  unauthenticated stream death it runs the same holder.refresh (single-flight)
 *  before re-opening. This is what makes a stale JWT (throttled refresh interval
 *  while the tab was hidden / machine asleep / a network change that killed
 *  the SDK's own refresh) self-heal instead of surfacing as a failed click. */
function authRetryInterceptor(holder: TokenHolder): Interceptor {
  return (next) => async (req) => {
    try {
      return await next(req);
    } catch (err) {
      const unauth = err instanceof ConnectError && err.code === Code.Unauthenticated;
      if (!unauth || req.stream || !holder.refresh) throw err;
      const fresh = await holder.refresh();
      if (!fresh) throw err;
      holder.token = fresh;
      req.header.set('Authorization', `Bearer ${fresh}`);
      return await next(req);
    }
  };
}

/** The api-gateway reads this to scope a subscription to ONE gateway, validated
 *  against the org's owned set (server-side `gwOverride`). Empty = server picks
 *  the org's default gateway (legacy single-gateway behavior). */
export const GATEWAY_HEADER = 'X-Vitesse-Gateway-Id';

/** Tag every request on this transport with its gateway id, so the api-gateway
 *  serves THAT gateway's data. Per-transport (not per-call) keeps the 18
 *  subscribe/fetch fns untouched; the browser still pools ONE HTTP/2 connection
 *  per origin, so N gateway-transports to the cloud ride a single multiplexed
 *  connection (the manager-pattern "one controlled connection"). */
function gatewayInterceptor(gatewayId: string): Interceptor {
  return (next) => async (req) => {
    if (gatewayId) req.header.set(GATEWAY_HEADER, gatewayId);
    return next(req);
  };
}

/** Build the Connect transport for a specific base URL (LAN or cloud — the
 *  Backend Worker races them and passes the winner) scoped to one `gatewayId`
 *  (empty = the org's server-resolved default). */
export function makeTransportFor(holder: TokenHolder, baseUrl: string, gatewayId = ''): Transport {
  return createConnectTransport({
    baseUrl,
    // authRetry wraps OUTSIDE headerInterceptor so its retried next(req)
    // re-runs with the refreshed Authorization header it just set.
    interceptors: [authRetryInterceptor(holder), headerInterceptor(holder), gatewayInterceptor(gatewayId)],
  });
}

/** Build the Connect transport. baseUrl: dev = same-origin (Vite proxy), prod = api-gateway. */
export function makeTransport(holder: TokenHolder): Transport {
  return makeTransportFor(holder, API_GATEWAY_URL);
}

/**
 * Build the CLOUD control-plane transport — for the FleetControl service only
 * (enroll / claim / fleet registry). Identical machinery + the SAME session-JWT
 * token holder as makeTransport; it differs ONLY in baseUrl (the cloud
 * api-gateway, derived in env). FleetControl is cloud-scoped, never edge-scoped,
 * so it must not ride the per-gateway local transport. Org scope comes from the
 * verified session JWT; no gateway id is ever sent.
 */
export function makeCloudTransport(holder: TokenHolder): Transport {
  return createConnectTransport({
    baseUrl: CLOUD_API_GATEWAY_URL,
    interceptors: [authRetryInterceptor(holder), headerInterceptor(holder)],
  });
}

// ── wire ControllerDescriptor → frontend Controller domain ───────────────────
function toController(id: string, d: ControllerDescriptor): Controller {
  return {
    id,
    name: d.displayName || id,
    protocol: d.driver,
    pluginId: d.pluginId || `${d.driver}-plugin`,
    status: (d.status || 'disconnected') as ConnectionStatus,
    discoveryStatus: (d.discoveryStatus || 'idle') as DiscoveryStatus,
    symbolCount: Number(d.symbolCount),
    address: d.address || '',
    model: d.model || '',
    firmware: d.firmware || '',
    errorDetail: d.errorDetail || '',
    properties: {
      tables: (d.properties?.tables ?? []).map((t) => ({
        title: t.title,
        columns: t.columns,
        rows: t.rows.map((r) => r.cells),
      })),
    },
  };
}

/*
 * Subscribe to controllers. Yields the FULL current set on every change.
 * Controllers are low-cardinality, so a fresh array per event is simpler than
 * threading deltas across the worker boundary. The api-gateway streams OP_PUT
 * for each existing controller (the snapshot) then live PUT/DELETE deltas; we
 * fold them into a running map and re-emit the whole set.
 */
export async function* subscribeControllers(transport: Transport, signal: AbortSignal): AsyncGenerator<Controller[]> {
  const client = createClient(Controllers, transport);
  const byId = new Map<string, Controller>();
  for await (const evt of client.subscribe({}, { signal })) {
    if (evt.op === ControllerEvent_Op.SNAPSHOT_COMPLETE) {
      // The snapshot is over — emit it even when it is empty. This is what tells
      // the manager the gateway is genuinely reachable: a box with no controllers
      // yet produces no PUTs, and without this frame it stayed "Connecting"
      // forever, looking exactly like a box that could not be reached.
      yield [...byId.values()];
      continue;
    }
    if (evt.op === ControllerEvent_Op.DELETE) {
      byId.delete(evt.controllerId);
    } else if (evt.descriptor) {
      byId.set(evt.controllerId, toController(evt.controllerId, evt.descriptor));
    } else {
      continue; // an op this client does not know: carries no set change
    }
    yield [...byId.values()];
  }
}

// ── cameras (Video.Subscribe = roster, Video.WatchCamera = consumer signaling) ─
function toCamera(id: string, d: CameraDescriptor): Camera {
  return {
    id,
    name: d.displayName || id,
    sourceKind: d.sourceKind || 'external',
    status: (d.streamStatus || 'idle') as CameraStatus,
    codec: d.codec || '',
    width: d.width,
    height: d.height,
    fps: d.fps,
    viewerCount: d.viewerCount,
    errorDetail: d.errorDetail || '',
    recordingEnabled: d.recordingEnabled,
    recordingPaused: d.recordingPaused,
    recordingCircular: d.recordingCircular,
    bytesUsed: Number(d.bytesUsed),
    bytesMax: Number(d.bytesMax),
    availableFromMs: d.availableFromNs ? Number(d.availableFromNs / 1_000_000n) : 0,
    availableToMs: d.availableToNs ? Number(d.availableToNs / 1_000_000n) : 0,
    // Backend-authored Info view — mirror the controller mapping (PropertyRow.cells
    // is the wire shape; the frontend renders it via <vitesse-properties-view>).
    properties: {
      tables: (d.properties?.tables ?? []).map((t) => ({
        title: t.title,
        columns: t.columns,
        rows: t.rows.map((r) => r.cells),
      })),
    },
  };
}

/*
 * Subscribe to a gateway's cameras. Yields the FULL current set on every change —
 * the same low-cardinality snapshot-then-deltas fold as controllers: the
 * api-gateway streams OP_PUT for each existing camera (the snapshot) then live
 * PUT/DELETE deltas; we fold them into a running map and re-emit the whole set.
 */
export async function* subscribeCameras(transport: Transport, signal: AbortSignal): AsyncGenerator<Camera[]> {
  const client = createClient(Video, transport);
  const byId = new Map<string, Camera>();
  for await (const evt of client.subscribe({}, { signal })) {
    if (evt.op === CameraEvent_Op.DELETE) {
      byId.delete(evt.cameraId);
    } else if (evt.descriptor) {
      byId.set(evt.cameraId, toCamera(evt.cameraId, evt.descriptor));
    }
    yield [...byId.values()];
  }
}

/*
 * Watch ONE camera: send the consumer's recvonly SDP offer, get the SFU's answer.
 * session-authed + gateway-scoped purely by the transport's interceptors (the same
 * Bearer + X-Vitesse-Gateway-Id every RPC gets, plus the unary unauthenticated-
 * retry). The MEDIA is WebRTC (DTLS-SRTP) and flows OUT-OF-BAND — only this SDP
 * exchange crosses Connect; the RTCPeerConnection + MediaStream live main-thread.
 */
export async function watchCamera(transport: Transport, cameraId: string, sdpOffer: string): Promise<string> {
  const reply = await createClient(Video, transport).watchCamera({ cameraId, inner: { sdpOffer } });
  if (resultErrorText(reply.result)) throw new Error(resultErrorText(reply.result));
  return reply.sdpAnswer;
}

/*
 * Snapshot ONE camera as a single encoded H264 keyframe — the CLIENT decodes it
 * (WebCodecs) into an image. atNs=0n ⇒ the latest LIVE frame (what it's showing
 * now); atNs>0 ⇒ the nearest recorded keyframe to that moment. The edge only
 * EXTRACTS the keyframe (no decode on the compute-strained source); turning the
 * one frame into pixels is the viewer's job, on the viewer's own idle hardware.
 * Feeds the AI's snapshot tool + the cloud poster. Bytes only — decode is separate
 * (decodeKeyframeToBlob) so a non-browser caller can use the encoded frame too.
 */
export interface CameraSnapshot {
  keyframe: Uint8Array; // Annex-B H264: SPS|PPS|IDR
  width: number;
  height: number;
  actualNs: bigint; // timestamp of the returned frame
  codec: string;
}

export async function cameraSnapshot(transport: Transport, cameraId: string, atNs: bigint): Promise<CameraSnapshot> {
  const reply = await createClient(Video, transport).snapshot({ cameraId, inner: { atNs } });
  if (resultErrorText(reply.result)) throw new Error(resultErrorText(reply.result));
  if (!reply.keyframe || reply.keyframe.length === 0) throw new Error('empty snapshot');
  return {
    keyframe: reply.keyframe,
    width: reply.width,
    height: reply.height,
    actualNs: reply.actualNs,
    codec: reply.codec,
  };
}

/*
 * Enable/disable recording for one camera. The video-engine flips
 * CameraDescriptor.recording_enabled and republishes, so the UI's roster
 * subscription delivers the new state — no optimistic update needed. Returns the
 * resulting recording state.
 */
export async function setCameraRecording(transport: Transport, cameraId: string, enabled: boolean): Promise<boolean> {
  const reply = await createClient(Video, transport).setRecording({ cameraId, inner: { enabled } });
  if (resultErrorText(reply.result)) throw new Error(resultErrorText(reply.result));
  return reply.enabled;
}

/** Pause/resume recording (without disabling). */
export async function setCameraRecordingPaused(transport: Transport, cameraId: string, paused: boolean): Promise<boolean> {
  const reply = await createClient(Video, transport).setRecordingPaused({ cameraId, inner: { paused } });
  if (resultErrorText(reply.result)) throw new Error(resultErrorText(reply.result));
  return reply.paused;
}

/** Overwrite-oldest (circular) vs stop-at-cap. */
export async function setCameraRecordingCircular(transport: Transport, cameraId: string, circular: boolean): Promise<boolean> {
  const reply = await createClient(Video, transport).setRecordingCircular({ cameraId, inner: { circular } });
  if (resultErrorText(reply.result)) throw new Error(resultErrorText(reply.result));
  return reply.circular;
}

/** One roster frame: the live integration set PLUS the creatable type catalog
 *  (with form fields). BOTH ride the same Integrations.Subscribe stream, so the
 *  Add-integration dialog reads the catalog the roster ALREADY received instead of
 *  opening its own (timeout-prone) fetch — one stream, no redundant round-trip. */
export interface IntegrationRoster {
  integrations: Integration[];
  catalogTypes: IntegrationCatalogType[];
}

/*
 * Subscribe to integrations (the 2nd integration API — DBs/brokers/REST/OT).
 * Identical fold to controllers: the api-gateway streams OP_PUT for each
 * existing integration (descriptor, then runtime) as the snapshot, then live
 * PUT/DELETE deltas; we merge descriptor (identity/type) + runtime (live status)
 * into one Integration per id and re-emit the whole set. The `catalog` payload
 * (the creatable TYPES) rides this SAME stream — captured here + surfaced on each
 * frame so the Add dialog consumes it without a separate fetch.
 */
export async function* subscribeIntegrations(transport: Transport, signal: AbortSignal): AsyncGenerator<IntegrationRoster> {
  const client = createClient(Integrations, transport);
  const byId = new Map<string, Integration>();
  // type key → the type's own properties, captured from the catalog payload (rides
  // this same stream). View kind, display label and group all belong to the TYPE,
  // not the instance, so we join them onto each instance here — that keeps the
  // catalog out of every consumer (a card can name and classify its own type
  // without holding 150 types to look one up).
  const typeInfo = new Map<string, { viewKind: IntegrationViewKind; label: string; group: string }>();
  let catalogTypes: IntegrationCatalogType[] = []; // the creatable types for the Add dialog
  const withType = (intg: Integration): Integration => {
    const info = typeInfo.get(intg.type);
    return info ? { ...intg, viewKind: info.viewKind, typeLabel: info.label, typeGroup: info.group } : intg;
  };
  for await (const evt of client.subscribe({}, { signal })) {
    if (evt.op === IntegrationEvent_Op.DELETE) {
      byId.delete(evt.integrationId);
    } else if (evt.payload.case === 'descriptor') {
      byId.set(evt.integrationId, withType(toIntegration(evt.integrationId, byId.get(evt.integrationId), evt.payload.value)));
    } else if (evt.payload.case === 'runtime') {
      byId.set(evt.integrationId, withType(toIntegration(evt.integrationId, byId.get(evt.integrationId), undefined, evt.payload.value)));
    } else if (evt.payload.case === 'catalog') {
      // Catalog snapshot → capture the per-type properties + the creatable types (for
      // the Add dialog), then back-fill any integrations already built before it
      // arrived (ordering between catalog + roster is not guaranteed).
      const types = evt.payload.value.types ?? [];
      for (const t of types) typeInfo.set(t.key, { viewKind: viewKindFromProto(t.viewKind), label: t.displayName, group: t.group });
      catalogTypes = types.map(toCatalogType);
      for (const [id, intg] of byId) byId.set(id, withType(intg));
    }
    yield { integrations: [...byId.values()], catalogTypes };
  }
}

/** One cursor-paged window of an integration target's ROWS (the table-viewport data
 *  grid). rows are plain records (cells unwrapped to JS). nextCursor / prevCursor
 *  continue the page forward / backward (""=at that boundary) — both O(log n) with
 *  keyset, so the grid can slide a bounded window in either direction at any depth. */
export interface IntegrationRowPage {
  rows: Array<Record<string, string | number | boolean | null>>;
  nextCursor: string;
  prevCursor: string;
  /** LEGACY: on the paged path this is the LENGTH OF THIS PAGE, not a total —
   *  see the wire comment on ReadReply.total_matching. Prefer totalRows. */
  totalMatching: number;
  /** The source honours row filters (driver-side predicate pushdown) — shapes
   *  the filter affordance BEFORE a click (Phase 4.4 UI). */
  filterable: boolean;
  /** True row count under the CURRENT filter, when the source can answer
   *  cheaply. undefined = UNKNOWN — never render a number for it, and never
   *  treat it as zero ("absent is not zero"). */
  totalRows?: number;
  /** The columns that identify ONE row of this target, for an in-place edit.
   *  EMPTY means this target cannot be edited — no key, a view, or a source with
   *  no update capability — and the UI must not offer the affordance (the same
   *  gated-UI rule `filterable` follows). */
  rowKey: string[];
}

/** One row predicate for a server-side filtered read — the browser-side lite of
 *  the wire's RowPredicate (integrations.proto). `value` is unused for the two
 *  null ops. The filter is EXACT-OR-REFUSE server-side: a source that cannot
 *  honour it refuses in prose, it is never silently ignored. */
export type RowFilterOp = 'eq' | 'neq' | 'lt' | 'lte' | 'gt' | 'gte' | 'contains' | 'isNull' | 'notNull';
export interface RowPredicateLite {
  column: string;
  op: RowFilterOp;
  value?: string | number | boolean;
}

const ROW_OP_WIRE: Record<RowFilterOp, RowPredicate_Op> = {
  eq: RowPredicate_Op.EQ,
  neq: RowPredicate_Op.NEQ,
  lt: RowPredicate_Op.LT,
  lte: RowPredicate_Op.LTE,
  gt: RowPredicate_Op.GT,
  gte: RowPredicate_Op.GTE,
  contains: RowPredicate_Op.CONTAINS,
  isNull: RowPredicate_Op.IS_NULL,
  notNull: RowPredicate_Op.NOT_NULL,
};

/** Shape a lite predicate value as the wire Value oneof. Integers ride intVal
 *  (exact), other numbers realVal — the driver binds either against a numeric
 *  column identically. */
function rowFilterWireValue(v: string | number | boolean): { v: NonNullable<Value['v']> } {
  if (typeof v === 'boolean') return { v: { case: 'boolVal', value: v } };
  if (typeof v === 'number') {
    return Number.isInteger(v)
      ? { v: { case: 'intVal', value: BigInt(v) } }
      : { v: { case: 'realVal', value: v } };
  }
  return { v: { case: 'stringVal', value: v } };
}

function toWireFilter(filter: RowPredicateLite[]) {
  return {
    predicates: filter.map((p) => ({
      column: p.column,
      op: ROW_OP_WIRE[p.op],
      ...(p.value !== undefined && p.op !== 'isNull' && p.op !== 'notNull'
        ? { value: rowFilterWireValue(p.value) }
        : {}),
    })),
  };
}

/** Unwrap a proto cell Value to a JS primitive for grid display. int64 stays exact:
 *  numeric when it fits a JS number (so the grid right-aligns it), else a string. */
function cellToJs(cell?: Value): string | number | boolean | null {
  if (!cell) return null;
  switch (cell.v.case) {
    case 'boolVal':
      return cell.v.value;
    case 'intVal': {
      const n = cell.v.value;
      return n >= BigInt(Number.MIN_SAFE_INTEGER) && n <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(n) : n.toString();
    }
    case 'realVal':
      return cell.v.value;
    case 'stringVal':
      return cell.v.value;
    case 'rawVal':
      return `⟨${cell.v.value.length} bytes⟩`;
    default:
      return null;
  }
}

/*
 * WRITE to an integration target (Integrations.Write → cmd.<gw>.integrations.<id>.write)
 * — the same verb and payload shape SCXML writes with, so there is ONE write path
 * into the service.
 *
 * Authorization is decided SERVER-SIDE and is not our business here: api-gateway
 * checks the capability against authoritative backend state before forwarding. A
 * PermissionDenied is therefore the authoritative answer, and its message is
 * already written for the user ("request access from your administrator") — so we
 * surface it verbatim. Hiding the control in the UI is a courtesy for people who
 * lack the capability, never the control itself.
 *
 * `values` follows the driver contract: column→value for a table, {"payload":…}
 * for a topic, {"body":…} for an endpoint.
 */
export async function integrationWrite(
  transport: Transport,
  integrationId: string,
  target: string,
  values: Record<string, string>,
): Promise<{ affected: number }> {
  const client = createClient(Integrations, transport);
  const inner = {
    target,
    values: Object.fromEntries(Object.entries(values).map(([k, v]) => [k, { v: { case: 'stringVal' as const, value: v } }])),
  };
  const reply = await client.write({ integrationId, inner });
  if (resultFailed(reply.result)) throw new Error(resultErrorText(reply.result) || 'integration write failed');
  return { affected: Number(reply.affected) };
}

/*
 * Read a cursor-paged window of an integration TARGET's rows (Integrations.Read →
 * cmd.<gw>.integrations.<id>.read, the table-viewport "show me this table's data"
 * grid). target = a discovered table/collection path; cursor = "" for the first
 * page, else the prior reply's nextCursor.
 */
export async function integrationReadRows(
  transport: Transport,
  integrationId: string,
  target: string,
  cursor: string,
  limit: number,
  filter?: RowPredicateLite[],
): Promise<IntegrationRowPage> {
  const client = createClient(Integrations, transport);
  const reply = await client.read({
    integrationId,
    inner: {
      source: { case: 'target', value: target },
      limit,
      cursor,
      // Cursors are filter-scoped (wire contract): the caller restarts from
      // cursor "" whenever the filter changes.
      ...(filter && filter.length > 0 ? { filter: toWireFilter(filter) } : {}),
    },
  });
  if (resultFailed(reply.result)) throw new Error(resultErrorText(reply.result) || 'integration read failed');
  return {
    rows: reply.rows.map(rowToJs),
    nextCursor: reply.nextCursor,
    prevCursor: reply.prevCursor,
    totalMatching: Number(reply.totalMatching),
    filterable: reply.filterable,
    // Presence-mapped: the wire field is optional, and absent means UNKNOWN.
    totalRows: reply.totalRows !== undefined ? Number(reply.totalRows) : undefined,
    rowKey: reply.rowKey ?? [],
  };
}

/*
 * Run a READ-ONLY query on an integration (Integrations.Read with a statement →
 * cmd.<gw>.integrations.<id>.read). The service runs it in a read-only transaction
 * and refuses a write. Each arg binds, as a string, to $1, $2, … of the statement.
 */
export async function integrationQuery(
  transport: Transport,
  integrationId: string,
  statement: string,
  args: string[],
  limit: number,
): Promise<IntegrationRowPage['rows']> {
  const client = createClient(Integrations, transport);
  const reply = await client.read({
    integrationId,
    inner: {
      source: { case: 'statement', value: statement },
      args: args.map((a) => ({ v: { case: 'stringVal' as const, value: a } })),
      limit,
    },
  });
  if (resultFailed(reply.result)) throw new Error(resultErrorText(reply.result) || 'integration query failed');
  return reply.rows.map(rowToJs);
}

/** 1 row of a read, with each cell as a JS primitive (cellToJs). */
function rowToJs(r: Row): Record<string, string | number | boolean | null> {
  const rec: Record<string, string | number | boolean | null> = {};
  for (const k of Object.keys(r.cells)) rec[k] = cellToJs(r.cells[k]);
  return rec;
}

/*
 * UPDATE one existing row of a table target — the table viewport's in-cell edit.
 *
 * The same Integrations.Write RPC and the same server-side capability gate as
 * integrationWrite above; the ONLY difference is that this one carries a `where`,
 * which is what turns an append into an edit. A source that cannot update in place
 * refuses in prose rather than inserting a duplicate row.
 *
 * `where` must address exactly one row. That is not enforced here — it CANNOT be,
 * since only the source knows what its predicates match — it is enforced by the
 * driver, which runs the statement in a transaction and rolls back if it changed
 * anything other than exactly one row. Build the predicate from the row's key
 * columns (see table-row-edit.ts).
 *
 * Values are typed per column rather than stringified (integrationWrite sends
 * everything as a string, which is right for a topic payload and wrong for an
 * integer column).
 */
export async function integrationUpdateRow(
  transport: Transport,
  integrationId: string,
  target: string,
  where: RowPredicateLite[],
  values: Record<string, string | number | boolean | null>,
): Promise<{ affected: number }> {
  if (where.length === 0) {
    // Never send an unaddressed edit: a `where`-less write is an INSERT on the
    // wire, so a bug that dropped the predicate would silently append a row.
    throw new Error('refusing to edit a row without identifying it');
  }
  const client = createClient(Integrations, transport);
  const reply = await client.write({
    integrationId,
    inner: {
      target,
      values: Object.fromEntries(
        Object.entries(values).map(([k, v]) => [k, v === null ? {} : rowFilterWireValue(v)]),
      ),
      where: toWireFilter(where),
    },
  });
  if (resultFailed(reply.result)) throw new Error(resultErrorText(reply.result) || 'integration update failed');
  return { affected: Number(reply.affected) };
}

/** One paged window of an integration's SCHEMA browse — tables (parent="") or one
 *  table's columns (parent="<table>"). nodes carry their path + a column count;
 *  next/prevCursor slide a bounded window over the table list (so a million-table
 *  schema is never materialized). The QUERYABLE-source metadata path. */
export interface SchemaBrowsePage {
  nodes: Array<{ path: string; kind: string; dataType: string; detail: string; readable: boolean; writable: boolean; childCount: number }>;
  nextCursor: string;
  prevCursor: string;
}

/*
 * Browse an integration's schema PAGED + SEARCHABLE (Integrations.BrowseSchema →
 * cmd.<gw>.integrations.<id>.browseSchema). parent="" pages tables (cursor + search);
 * parent="<table>" lazy-loads that one table's columns. Nothing is materialized — the
 * navigator holds only the viewport, so it scales to millions of tables (the symbol
 * paradigm, a layer lower: query exactly what's needed, cache nothing).
 */
export async function browseIntegrationSchema(
  transport: Transport,
  integrationId: string,
  parent: string,
  cursor: string,
  search: string,
  limit: number,
): Promise<SchemaBrowsePage> {
  const client = createClient(Integrations, transport);
  const reply = await client.browseSchema({ integrationId, parent, cursor, search, limit });
  if (resultFailed(reply.result)) throw new Error(resultErrorText(reply.result) || 'schema browse failed');
  const nodes = reply.nodes.map((n) => ({
    path: n.path,
    kind: n.item?.kind ?? '',
    dataType: n.item?.dataType ?? '',
    detail: n.item?.detail ?? '',
    readable: n.item?.readable ?? false,
    writable: n.item?.writable ?? false,
    childCount: n.childCount,
  }));
  return { nodes, nextCursor: reply.nextCursor, prevCursor: reply.prevCursor };
}

/*
 * Subscribe to ONE integration's IP-safe log tail (Integrations.SubscribeLogs:
 * recent-window replay then live), reusing the ControllerLog line shape (it's a
 * generic log line — source/message/body/attrs). The detail page's Logs tab maps
 * it to the uniform LogLine via the shared <vitesse-log-tree>.
 */
export async function* subscribeIntegrationLogs(
  transport: Transport,
  integrationId: string,
  signal: AbortSignal,
): AsyncGenerator<ControllerLog> {
  const client = createClient(Integrations, transport);
  for await (const ev of client.subscribeLogs({ integrationId }, { signal })) {
    yield {
      tsMs: Number(ev.timestampNs / 1_000_000n),
      severity: ev.severity || 'info',
      source: ev.source,
      message: ev.message,
      body: ev.body,
      attrs: ev.attrs ?? {},
    };
  }
}

/** Merge a descriptor and/or runtime delta into the running Integration, keeping
 *  whichever half this event didn't carry. Descriptor sets identity + type;
 *  runtime sets live status/error/summary. */
/** proto ViewKind enum → the proto-agnostic domain union — the single classifier:
 *  TOPIC → live-value tree, TABLE → navigator + grid, ENDPOINT → request builder.
 *  UNSPECIFIED collapses to 'unspecified' (the detail page shows loading). */
function viewKindFromProto(v: ViewKind): IntegrationViewKind {
  switch (v) {
    case ViewKind.TOPIC:
      return 'topic';
    case ViewKind.TABLE:
      return 'table';
    case ViewKind.ENDPOINT:
      return 'endpoint';
    default:
      return 'unspecified';
  }
}

function toIntegration(id: string, prev: Integration | undefined, d?: IntegrationDescriptor, rt?: IntegrationRuntime): Integration {
  return {
    id,
    displayName: d?.displayName || prev?.displayName || id,
    description: d?.description ?? prev?.description ?? '',
    type: d?.type || prev?.type || '',
    // All three are set from the catalog payload (same stream) by
    // subscribeIntegrations; carried forward across descriptor/runtime updates
    // until then. typeLabel/typeGroup stay '' if the catalog never arrives, and
    // every consumer falls back on the raw `type` key rather than showing a blank.
    viewKind: prev?.viewKind ?? 'unspecified',
    typeLabel: prev?.typeLabel ?? '',
    typeGroup: prev?.typeGroup ?? '',
    // Descriptor-only (no runtime yet) → honestly 'connecting'; runtime overrides.
    status: (rt?.status || prev?.status || 'connecting') as ConnectionStatus,
    errorDetail: rt?.errorDetail ?? prev?.errorDetail ?? '',
    summary: rt?.summary || prev?.summary || '',
    fields: d?.fields ?? prev?.fields ?? {},
    schemaCount: prev?.schemaCount ?? 0,
    // Hand-added operations ride the descriptor; a runtime-only frame keeps
    // the previous list.
    targets: d
      ? d.targets.map((nt) => ({
          name: nt.name,
          // UNSPECIFIED is meaningful, not a fallback: a broker topic is
          // read-write, and collapsing it to 'read' here would make a hand-added
          // topic render as non-publishable on the round trip.
          mode: nt.mode === NamedTarget_Mode.WRITE ? 'write' : nt.mode === NamedTarget_Mode.READ ? 'read' : 'readwrite',
          spec: nt.spec,
        }))
      : (prev?.targets ?? []),
    // The Forwards ride the descriptor and their status rides the runtime, so
    // each frame keeps the half that it does not carry.
    forwards: d
      ? d.forwards.map((f) => ({
          name: f.name,
          // A Forward saved before the list existed has 1 `selector`.
          selectors: f.selectors.length ? [...f.selectors] : f.selector ? [f.selector] : [],
          target: f.target,
        }))
      : (prev?.forwards ?? []),
    forwardStatus: rt
      ? rt.forwards.map((f) => ({
          name: f.name,
          state: f.state,
          // uint64 and int64 arrive as bigint, which JSON cannot hold.
          lag: Number(f.lag),
          lastWriteMs: Number(f.lastWriteNs / 1_000_000n),
          lastError: f.lastError,
          lastErrorMs: Number(f.lastErrorNs / 1_000_000n),
        }))
      : (prev?.forwardStatus ?? []),
  };
}

// ── integration catalog + apply (the Add-integration flow) ───────────────────

/** One creatable integration type for the Add dialog — the connection form is
 *  pre-adapted to the schema-form's `FormFieldSpec` shape so the dialog renders
 *  it with the same <vitesse-schema-form> the Add-controller flow uses. */
export interface IntegrationCatalogType {
  key: string; // "postgres" | "mqtt" | …
  displayName: string;
  group: string;
  /** The kind this type is — the classifier a tier's ceilings key off, so the
   *  picker can say "your plan is full of these" before anything is typed. */
  viewKind: IntegrationViewKind;
  form: FormFieldSpec[];
}

// The service's FieldSpec.component is a string; the schema-form wants the
// FieldComponent enum. Whether a field is SECRET comes from the server's own flag
// (carried through below), not from this map — the map only picks the widget.
const FIELD_COMPONENT_BY_NAME: Record<string, FieldComponent> = {
  text: FieldComponent.TEXT,
  number: FieldComponent.NUMBER,
  select: FieldComponent.SELECT,
  radio: FieldComponent.RADIO,
  toggle: FieldComponent.TOGGLE,
  secret: FieldComponent.SECRET,
  pem: FieldComponent.PEM,
};

function toCatalogType(t: IntegrationType): IntegrationCatalogType {
  return {
    key: t.key,
    displayName: t.displayName,
    group: t.group,
    viewKind: viewKindFromProto(t.viewKind),
    form: t.fields.map((f) => ({
      key: f.key,
      component: FIELD_COMPONENT_BY_NAME[f.component] ?? FieldComponent.TEXT,
      label: f.label,
      required: f.required,
      default: '',
      placeholder: f.placeholder,
      help: '',
      validation: '',
      options: f.options ?? [],
      // The three the browser used to throw away. `secret` decides the sealed
      // bucket; `group` sections the form; showWhen hides what the chosen mode
      // does not read (integration-form-model.ts).
      secret: f.secret,
      group: f.group,
      showWhen: f.showWhenKey ? { key: f.showWhenKey, values: f.showWhenValues ?? [] } : undefined,
    })),
  };
}

/*
 * One-shot read of the integration TYPE catalog (the Add-integration form
 * source) off the SAME Integrations.Subscribe stream the roster uses — the
 * catalog rides it as a `catalog` payload in the snapshot. Opens the stream,
 * returns the first catalog event mapped to the schema-form field shape, then
 * aborts. A 5s guard returns [] if the catalog never arrives (a service that
 * hasn't published it) so the dialog falls back to its empty state.
 */
export async function getIntegrationCatalog(transport: Transport): Promise<IntegrationCatalogType[]> {
  const ac = new AbortController();
  const guard = setTimeout(() => ac.abort(), 5000);
  try {
    for await (const evt of createClient(Integrations, transport).subscribe({}, { signal: ac.signal })) {
      if (evt.payload.case === 'catalog') {
        return (evt.payload.value.types ?? []).map(toCatalogType);
      }
    }
    return [];
  } catch {
    return []; // aborted / stream error → empty catalog
  } finally {
    clearTimeout(guard);
    ac.abort();
  }
}

/*
 * Apply (create/edit) an integration via Integrations.Apply. `validateOnly` is
 * the pre-save "Test connection" (connect + validate, don't persist). Secrets
 * ride plaintext over the TLS hop; the service seals them at rest + strips them
 * from the roster stream. Returns the service's ok/error verdict.
 */
export async function applyIntegration(
  transport: Transport,
  req: {
    integrationId: string;
    type: string;
    displayName: string;
    fields: Record<string, string>;
    secrets: Record<string, string>;
    description?: string;
    validateOnly?: boolean;
    /** Field-mask edit: overlay ONLY these descriptor fields (e.g. ['description'])
     *  onto the persisted one — no secret re-entry, no reconnect. Empty/absent =
     *  a full apply. */
    updateMask?: string[];
    /** Hand-added operations (endpoint kind). A full apply REPLACES the
     *  descriptor — pass the current list through on every re-apply. */
    targets?: IntegrationOp[];
  },
): Promise<{ ok: boolean; error: string }> {
  const reply = await createClient(Integrations, transport).apply({
    integrationId: req.integrationId,
    inner: {
      descriptor: {
        displayName: req.displayName,
        type: req.type,
        fields: req.fields,
        secrets: req.secrets,
        description: req.description ?? '',
        targets: (req.targets ?? []).map((op) => ({
          name: op.name,
          mode:
            op.mode === 'write'
              ? NamedTarget_Mode.WRITE
              : op.mode === 'read'
                ? NamedTarget_Mode.READ
                : NamedTarget_Mode.UNSPECIFIED,
          spec: op.spec,
          options: {},
          params: [],
        })),
      },
      validateOnly: req.validateOnly ?? false,
      updateMask: req.updateMask ?? [],
    },
  });
  return { ok: !resultFailed(reply.result), error: resultErrorText(reply.result) };
}

/*
 * Delete (tear down + purge) an integration via Integrations.Delete. The service
 * stops the driver, drops its leases/forwards, and removes its whole state
 * subtree; the roster stream then delivers the removal.
 */
export async function deleteIntegration(transport: Transport, integrationId: string): Promise<{ ok: boolean; error: string }> {
  const reply = await createClient(Integrations, transport).delete({ integrationId });
  return { ok: !resultFailed(reply.result), error: resultErrorText(reply.result) };
}

/*
 * Add 1 Forward to an integration via Integrations.PutForward. The service
 * checks it, stores it and starts it. The other Forwards and the connection do
 * not change. `message` is the summary of the service: it says whether the
 * Forward runs now or starts when the integration connects.
 */
export async function putForward(
  transport: Transport,
  integrationId: string,
  forward: IntegrationForward,
): Promise<{ ok: boolean; error: string; message: string }> {
  const reply = await createClient(Integrations, transport).putForward({
    integrationId,
    inner: { forward: { name: forward.name, selectors: forward.selectors, target: forward.target } },
  });
  return { ok: !resultFailed(reply.result), error: resultErrorText(reply.result), message: reply.result?.summary ?? '' };
}

/*
 * Remove 1 Forward of an integration, by its name, via
 * Integrations.DeleteForward. The service stops it and deletes its durable.
 */
export async function deleteForward(
  transport: Transport,
  integrationId: string,
  name: string,
): Promise<{ ok: boolean; error: string; message: string }> {
  const reply = await createClient(Integrations, transport).deleteForward({ integrationId, inner: { name } });
  return { ok: !resultFailed(reply.result), error: resultErrorText(reply.result), message: reply.result?.summary ?? '' };
}

// ── symbol catalog (SymbolIndex.Subscribe) ───────────────────────────────────
export interface SymbolSummary {
  totalInScope: number;
  samplePaths: string[];
}

/** Subscribe to a controller's symbol catalog; yields a summary per SymbolPage. */
export async function* subscribeSymbols(transport: Transport, controllerId: string, signal: AbortSignal): AsyncGenerator<SymbolSummary> {
  const client = createClient(SymbolIndex, transport);
  for await (const page of client.subscribe({ controllerId, pageSize: 30 }, { signal })) {
    yield { totalInScope: page.totalInScope, samplePaths: page.rows.slice(0, 30).map((r) => r.path) };
  }
}

/** One symbol's catalog row, decoded — the shape the grid's tree model consumes. */
export interface CatalogRow {
  /** Decoded dotted path (e.g. "DB1.Config.Speed") — the grid's symbol id. */
  path: string;
  /** Direct-child count; >0 means this node is a container (object/array). */
  childCount: number;
  /** Vendor/logical type string from the SymbolDescriptor ("BOOL", "REAL", …). */
  dataType: string;
  /** "r" | "w" | "rw". */
  permission: string;
  /** User-set overlay label (empty if none). */
  label: string;
  /** Priority-polling flag (backend-confirmed) — drives the red "!" persistence. */
  priority: boolean;
  /** Write-lock flag (backend-confirmed) — drives the padlock state persistence. */
  locked: boolean;
  /** User-deletable leaf (plugin-decided) — drives the row's trash affordance. */
  deletable: boolean;
  /** REST/integration "METHOD /path" spec (empty for PLC symbols) — drives the
   *  endpoint viewport's method badge + the builder's URL bar. */
  detail: string;
  /** Source kind: "endpoint" (invocable REST op), "object" (a container value —
   *  a whole row/message/response), "field"/"" (a leaf). Empty for PLC symbols.
   *  Lets the endpoint viewport render op-rows vs field-leaves. */
  kind: string;
}

// ── viewport-paged tree (the stateless, blazing-fast path) ───────────────────
// The browser never materializes the catalog. It asks the indexer for ONE
// linearized viewport page at a time; the indexer (which synthesizes the tree
// server-side) returns the visible window + prefetched children for collapsed
// nodes + keyset cursors. One request/reply per buffer-edge / expand / filter.

/** Keyset cursor — opaque position in the sorted tree (anchor row's sort key). */
export interface PageCursorLite {
  anchorPath: string;
  anchorSymbolId: string;
  /** 0/1 = forward, 2 = backward (PageCursor_Direction). */
  direction: number;
}

/** What the IndexerPagedView sends per fetch — mirrors SymbolQuery's tree fields. */
export interface PageQuery {
  expandedPaths?: string[];
  /** Flat-mode collapse blacklist — descendants of these parents are hidden. */
  collapsedPaths?: string[];
  fts?: string;
  pageCursor?: PageCursorLite;
  viewportAnchorPath?: string;
  viewportSize?: number;
  pageSize?: number;
  /** Random-access jump: skip the first N matching rows (scrollbar drag). */
  offset?: number;
  /** Window-follow correlation id (see subscribeTypedValues' followSession). */
  clientSession?: string;
}

/** One prefetched-children group — a collapsed parent's first N children. */
export interface PrefetchGroup {
  parentPath: string;
  leafNames: string[];
  childCounts: number[];
  dataTypes: string[];
  permissions: string[];
  labels: string[];
  details: string[];
  kinds: string[];
}

/** A linearized viewport page — the only tree data the browser ever holds. */
export interface SymbolPageData {
  rows: CatalogRow[];
  prefetched: PrefetchGroup[];
  nextCursor?: PageCursorLite;
  prevCursor?: PageCursorLite;
  totalMatching: number;
  totalInScope: number;
  /** Parents whose children were CUT SHORT by the indexer's window cap — this
   *  page's rows for them are a PREFIX of their true children, and the cursors
   *  cannot signal it ("end of page" and "end of window" look identical). The
   *  UI must say "showing the first N — narrow the search", never render the
   *  listing as complete. Empty for PLC/materialize catalogs. */
  truncatedParents: string[];
}

function cursorOut(cur?: { anchorPath: string; anchorSymbolId: string; direction: number }): PageCursorLite | undefined {
  if (!cur || !cur.anchorPath) return undefined;
  return { anchorPath: cur.anchorPath, anchorSymbolId: cur.anchorSymbolId, direction: cur.direction };
}

/** Build the wire SymbolQuery from a PageQuery — shared by the one-shot fetch and
 *  the persistent stream so they query byte-identically (same clientSession ⇒ the
 *  api-gateway re-scopes the value plane from either, the same way). */
function buildPageQuery(scopeId: string, q: PageQuery) {
  return {
    controllerId: scopeId,
    clientSession: q.clientSession ?? '',
    pageSize: q.pageSize ?? 500,
    expandedPaths: q.expandedPaths ?? [],
    collapsedPaths: q.collapsedPaths ?? [],
    fts: q.fts ?? '',
    offset: q.offset ?? 0,
    viewportAnchorPath: q.viewportAnchorPath ?? '',
    viewportSize: q.viewportSize ?? 0,
    ...(q.pageCursor
      ? {
          pageCursor: {
            anchorPath: q.pageCursor.anchorPath,
            anchorSymbolId: q.pageCursor.anchorSymbolId,
            direction: q.pageCursor.direction,
          },
        }
      : {}),
  };
}

/** Decode a wire SymbolPage → the SymbolPageData the grid consumes. */
function mapSymbolPage(page: SymbolPage): SymbolPageData {
  return {
    rows: page.rows.map((r) => ({
      path: r.path,
      childCount: r.childCount,
      dataType: r.dataType,
      permission: r.permission,
      label: r.label,
      priority: r.priority,
      locked: r.locked,
      deletable: r.deletable,
      detail: r.detail,
      kind: r.kind,
    })),
    prefetched: page.prefetchedChildren.map((p) => ({
      parentPath: p.parentPath,
      leafNames: p.leafNames,
      childCounts: p.childCounts,
      dataTypes: p.dataTypes,
      permissions: p.permissions,
      labels: p.labels,
      details: p.details,
      kinds: p.kinds,
    })),
    nextCursor: cursorOut(page.nextCursor),
    prevCursor: cursorOut(page.prevCursor),
    totalMatching: page.totalMatching,
    totalInScope: page.totalInScope,
    truncatedParents: page.truncatedParents,
  };
}

/**
 * Fetch ONE viewport page for a tree query, from a paged-index service. Opens
 * the service's Subscribe stream, takes the first (snapshot) frame, and closes —
 * request/reply shape. (See subscribeSymbolPageStreamFrom for the KEEP-OPEN
 * variant the grid uses to follow live catalog growth with no re-fetch.)
 * SymbolIndex (controllers) and IntegrationIndex (integrations) share the EXACT
 * `SymbolQuery → stream SymbolPage` contract — the proto reuses the same messages
 * — so one helper serves both; `scopeId` is the controller id or the integration
 * id (the wire field is named `controllerId` either way).
 */
export async function fetchPageFrom(
  client: Client<typeof SymbolIndex>,
  scopeId: string,
  q: PageQuery,
  signal: AbortSignal,
): Promise<SymbolPageData> {
  /*
   * A ONE-SHOT PAGE FETCH ABSORBS A TRANSIENT MISS RATHER THAN SURFACING IT.
   *
   * `deadline_exceeded` and `unavailable` mean the request did not get through or did not come back
   * in time — the intermittent browser→relay leg (result.ts calls these the two canonical transient
   * codes), not a real absence: the indexer sits idle and the api-gateway logs nothing while they
   * happen. Surfaced raw, a search that would have succeeded on the next try becomes a red tool call,
   * and the model reports a broken controller for what was a dropped packet. Observed live: a
   * dashboard designer whose symbol search failed three times in a row on a healthy relay, tripping
   * the run's tool breaker.
   *
   * A page fetch is idempotent and read-only, so a few short-backoff retries turn a flaky leg into a
   * slightly slower answer. Only the transient codes retry; a real refusal (permission, plan, a bad
   * query) throws on the first attempt, and a genuine upstream abort is never retried.
   *
   * The window is sized to ride out a MULTI-SECOND stall, not just a dropped packet: observed live, a
   * dashboard designer's first search `deadline_exceeded`d through a short retry burst, then the very
   * next search (seconds later) returned normally — a stall that outlasted four quick tries but was
   * gone within a few seconds. Six attempts on a growing backoff (capped) span ~4s of waiting plus
   * the per-attempt request time, so that same stall is absorbed instead of surfacing as a red call.
   * A stall longer than the whole window is an unhealthy relay, not a blip, and correctly surfaces.
   */
  const MAX_ATTEMPTS = 6;
  const isTransient = (err: unknown): boolean =>
    err instanceof ConnectError && (err.code === Code.Unavailable || err.code === Code.DeadlineExceeded);
  let lastErr: unknown = new Error('indexer closed stream before first page');
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    if (signal.aborted) throw new DOMException('the fetch was aborted', 'AbortError');
    // The Subscribe RPC is a LIVE server-stream (snapshot then deltas). To turn it into one
    // request/reply we capture the first frame then abort + break. That abort makes connect-web throw
    // `[canceled]` from the iterator's cleanup, which is EXPECTED — so we swallow it once we already
    // hold a page. The parent `signal` is linked so a real upstream abort still propagates.
    const ac = new AbortController();
    const onParentAbort = () => ac.abort();
    signal.addEventListener('abort', onParentAbort, { once: true });
    let result: SymbolPageData | undefined;
    try {
      for await (const page of client.subscribe(buildPageQuery(scopeId, q), { signal: ac.signal })) {
        result = mapSymbolPage(page);
        ac.abort();
        break;
      }
    } catch (err) {
      if (!result) {
        // A genuine upstream abort, or a non-transient refusal, is final — do not retry it.
        if (signal.aborted || !isTransient(err)) throw err;
        lastErr = err;
      }
      // else: our own post-capture abort, and we already hold the page.
    } finally {
      signal.removeEventListener('abort', onParentAbort);
    }
    if (result) return result;
    // Transient miss (a retryable error, or a stream that closed before its first frame). Back off on
    // a growing delay — 200,400,600,800,1000ms, capped — so a stall lasting a couple of seconds is
    // still caught while a healthy relay answers on the first or second try with almost no added wait.
    if (attempt < MAX_ATTEMPTS - 1) await new Promise((r) => setTimeout(r, Math.min(1000, 200 * (attempt + 1))));
  }
  throw lastErr;
}

/**
 * KEEP-OPEN variant: opens the Subscribe stream and yields the snapshot frame
 * PLUS every subsequent recomputed page the indexer pushes. The indexer re-runs
 * the session's query + hash-compares server-side on any catalog mutation and
 * pushes only when the page actually changed (see nats-fabric-symbol-indexer.md),
 * so the grid is a DUMB, STATELESS viewport: it renders whatever page is pushed
 * and never decides "did it change" — the backend owns that. The query carries
 * clientSession, so the api-gateway re-scopes the value plane from each pushed
 * page's leaves (followLeaves), exactly as for the one-shot fetch — so live values
 * follow catalog growth for free. Ends when `signal` aborts (the view re-queries
 * on nav, or is destroyed).
 */
async function* subscribeSymbolPageStreamFrom(
  client: Client<typeof SymbolIndex>,
  scopeId: string,
  q: PageQuery,
  signal: AbortSignal,
): AsyncGenerator<SymbolPageData> {
  for await (const page of client.subscribe(buildPageQuery(scopeId, q), { signal })) {
    yield mapSymbolPage(page);
  }
}

/** Controller symbol-catalog page (SymbolIndex). */
export async function fetchSymbolPage(
  transport: Transport,
  controllerId: string,
  q: PageQuery,
  signal: AbortSignal,
): Promise<SymbolPageData> {
  return fetchPageFrom(createClient(SymbolIndex, transport), controllerId, q, signal);
}

/** Controller symbol-catalog page STREAM (SymbolIndex) — snapshot + live deltas. */
export function subscribeSymbolPageStream(
  transport: Transport,
  controllerId: string,
  q: PageQuery,
  signal: AbortSignal,
): AsyncGenerator<SymbolPageData> {
  return subscribeSymbolPageStreamFrom(createClient(SymbolIndex, transport), controllerId, q, signal);
}

/** Integration schema page (IntegrationIndex) — identical wire contract to the
 *  symbol catalog, integration-scoped (the integration-indexer keys its store by
 *  the integration id carried in SymbolQuery.controllerId). */
export async function fetchIntegrationPage(
  transport: Transport,
  integrationId: string,
  q: PageQuery,
  signal: AbortSignal,
): Promise<SymbolPageData> {
  // IntegrationIndex reuses SymbolIndex's exact Subscribe(SymbolQuery) → stream
  // SymbolPage contract, so the client is interchangeable for paging (the cast
  // bridges the two nominally-distinct-but-wire-identical service descriptors).
  return fetchPageFrom(createClient(IntegrationIndex, transport) as unknown as Client<typeof SymbolIndex>, integrationId, q, signal);
}

/** Integration schema page STREAM (IntegrationIndex) — snapshot + live deltas;
 *  same wire contract as the symbol catalog. */
export function subscribeIntegrationPageStream(
  transport: Transport,
  integrationId: string,
  q: PageQuery,
  signal: AbortSignal,
): AsyncGenerator<SymbolPageData> {
  return subscribeSymbolPageStreamFrom(
    createClient(IntegrationIndex, transport) as unknown as Client<typeof SymbolIndex>,
    integrationId,
    q,
    signal,
  );
}

/**
 * Toggle priority-polling for symbols on their plugin. Hints the plugin to poll
 * the given symbols faster; the descriptor's `priority` flag is updated +
 * republished. symbol paths are encoded to subject sids per the wire contract.
 *
 * `pluginId` is the multiplexed plugin container that owns the controller — one
 * container per protocol holds N controllers (the cmd subject is
 * `cmd.<gw>.plugins.<pluginId>.setPollingPriority`). It's derived from the
 * controller's driver (`<driver>-plugin`, e.g. "mock" → "mock-plugin"), NOT the
 * controller_id.
 */
export async function setPollingPriority(
  transport: Transport,
  pluginId: string,
  controllerId: string,
  symbolPaths: string[],
  priority: boolean,
): Promise<SymbolOpResult[]> {
  const client = createClient(Plugins, transport);
  const reply = await client.setPollingPriority({
    pluginId,
    inner: { controllerId, entries: symbolPaths.map((p) => ({ symbolId: encodeSymbolPath(p), priority })) },
  });
  return (reply.results ?? []).map((r) => ({ symbolId: r.symbolId, ok: r.ok, error: r.error, errorCode: '' }));
}

/**
 * Lock (or unlock) symbols for WRITE protection. The owning plugin is the sole
 * authority: it stores the lock, sets SymbolDescriptor.locked, and refuses
 * writes to locked symbols. `paths` are decoded; we encode to the subject id.
 * Returns per-symbol results (callers that only toggle one row may ignore them).
 */
export async function setSymbolsLocked(
  transport: Transport,
  pluginId: string,
  controllerId: string,
  symbolPaths: string[],
  locked: boolean,
): Promise<SymbolOpResult[]> {
  const client = createClient(Plugins, transport);
  // ONE verb — `locked` is the parameter (no lockSymbols/unlockSymbols twins).
  const inner = { controllerId, symbolIds: symbolPaths.map(encodeSymbolPath), locked };
  const reply = await client.setSymbolLock({ pluginId, inner });
  return (reply.results ?? []).map((r) => ({ symbolId: r.symbolId, ok: r.ok, error: r.error, errorCode: r.errorCode }));
}

// ── whole-controller bulk (Plugins.ApplyLockToAll / ApplyPriorityToAll) ──────

/** The four whole-controller bulk ops, in the grid's domain vocabulary. */
export type BulkOp = 'lock' | 'unlock' | 'prioritize' | 'clearPriority';

/** Bulk result. `affected` = symbols the plugin touched (all-or-nothing at the
 *  controller level, so no per-symbol failure list). */
export interface ApplyToAllOutcome {
  ok: boolean;
  affected: number;
  error: string;
}

/**
 * Apply ONE op to EVERY symbol on a controller — the horizontal whole-controller
 * path. The plugin flips the flag across the entire catalog and republishes in one
 * hardened burst, so "select all, no filter" is a SINGLE RPC instead of
 * enumerating + chunking the catalog (a 500k-symbol lock → one call). Explicit or
 * filtered selections take the chunked per-id path (runChunkedSymbolOp). The
 * gateway gives these a 2-minute deadline because the whole-catalog republish is
 * the slow part.
 *
 * TWO RPCs, because the two planes differ and the gate is per-procedure.
 *
 * There was one `applyToAll` carrying the op as a proto enum, and it was gated on
 * `symbol:lock` alone — so bulk-prioritizing bypassed the `telemetry:flows` PLAN
 * that refuses the same change one symbol at a time. This function is the caller
 * that made that reachable in the product: the grid routes `prioritize` and
 * `clearPriority` here whenever "select all" is active with no filter, which is
 * exactly the 500k-symbol case. Locking is a permission; prioritizing is priced;
 * routing each to the verb that carries its own gate is the whole fix.
 */
export async function applyToAll(transport: Transport, pluginId: string, controllerId: string, op: BulkOp): Promise<ApplyToAllOutcome> {
  const client = createClient(Plugins, transport);
  const reply =
    op === 'lock' || op === 'unlock'
      ? await client.applyLockToAll({ pluginId, inner: { controllerId, locked: op === 'lock' } })
      : await client.applyPriorityToAll({ pluginId, inner: { controllerId, priority: op === 'prioritize' } });
  return { ok: !resultFailed(reply.result), affected: Number(reply.affected), error: resultErrorText(reply.result) };
}

// ── symbol writes (Plugins.WriteSymbols — the one plural write path) ──────────

/** A decoded JS value already coerced to the symbol's type by the caller. */
export type SymbolWriteValue = boolean | number | bigint | string;

export interface SymbolWrite {
  path: string; // decoded path; encoded to a subject sid on the wire
  value: SymbolWriteValue; // already type-coerced
  dataType: string; // 'boolean'|'integer'|'number'|'string'|'object'|'array'
}

/** Per-symbol outcome shared by write / lock / unlock (mirrors the proto results). */
export interface SymbolOpResult {
  symbolId: string; // subject-encoded id (what the wire + plugin speak)
  ok: boolean;
  error: string; // human/AI-readable; empty if ok
  errorCode: string; // machine code (e.g. "LOCKED"); empty if ok
}

/** Build a SymbolValue protobuf init from a coerced JS value + its data type.
 * Return type is inferred (not annotated) so each branch keeps its narrow
 * oneof case — protobuf-es requires case↔value-type correlation. */
function symbolValueInit(value: SymbolWriteValue, dataType: string) {
  switch (dataType) {
    case 'boolean':
      return { value: { case: 'boolVal' as const, value: Boolean(value) } };
    case 'integer':
      return { value: { case: 'intVal' as const, value: BigInt(Math.trunc(Number(value))) } };
    case 'number':
      return { value: { case: 'realVal' as const, value: Number(value) } };
    case 'object':
    case 'array':
      // No structured case on the wire — serialize to JSON string. The plugin
      // round-trips it; structural writes are an edge case (most writables are scalars).
      return { value: { case: 'stringVal' as const, value: typeof value === 'string' ? value : JSON.stringify(value) } };
    default: // 'string' and anything unknown
      return { value: { case: 'stringVal' as const, value: String(value) } };
  }
}

/**
 * Write a batch of symbol values via the single plural RPC. ONE entry writes one
 * symbol; N entries write a batch. The api-gateway caps a call at 1000 entries —
 * large bulk writes must chunk client-side (see runChunkedSymbolOp). Returns the
 * plugin's per-symbol results (ok + structured error_code/error for LOCKED etc.).
 */
export async function writeSymbols(
  transport: Transport,
  pluginId: string,
  controllerId: string,
  writes: SymbolWrite[],
): Promise<SymbolOpResult[]> {
  const client = createClient(Plugins, transport);
  const reply = await client.writeSymbols({
    pluginId,
    inner: {
      controllerId,
      writes: writes.map((w) => ({ symbolId: encodeSymbolPath(w.path), value: symbolValueInit(w.value, w.dataType) })),
    },
  });
  return (reply.results ?? []).map((r) => ({ symbolId: r.symbolId, ok: r.ok, error: r.error, errorCode: r.errorCode }));
}

// ── plugin types (Plugins.Subscribe) — the backend-driven "Add controller" picker ──

export { FieldComponent };

/** One field of a plugin's declarative form, decoded for the schema renderer. */
export interface FormFieldSpec {
  key: string;
  component: FieldComponent;
  label: string;
  required: boolean;
  default: string;
  placeholder: string;
  help: string;
  validation: string;
  options: string[];
  /** The server's word on whether this value is sealed. Optional because plugin
   *  forms predate it; absent means "by widget" (integration-form-model.isSecret). */
  secret?: boolean;
  /** Section heading ("Connection" | "Auth" | "TLS"). Absent: unsectioned. */
  group?: string;
  /** Show only while `key`'s current value is one of `values`. */
  showWhen?: { key: string; values: string[] };
}

/** A plugin type + its capabilities/forms — what the add-controller picker shows. */
export interface PluginType {
  pluginId: string; // the <pid> the add/remove RPCs target
  displayName: string;
  canManuallyAdd: boolean;
  canManuallyDelete: boolean;
  canAddSymbols: boolean;
  /*
   * The plugin ENFORCES the symbol write-lock. Carried because the padlock is not
   * a UI preference: the lock is refused at the plugin's write path, below every
   * role and every cloud-side caller, which is what makes it the right protection
   * for an estop. A plugin that has not implemented it cannot refuse anything, so
   * a padlock against it would look like protection and be none.
   */
  canLockSymbols: boolean;
  connectionForm: FormFieldSpec[];
  addSymbolForm: FormFieldSpec[];
}

function toFormFields(s: { fields?: readonly FormFieldSpec[] } | undefined): FormFieldSpec[] {
  return (s?.fields ?? []).map((f) => ({
    key: f.key,
    component: f.component,
    label: f.label,
    required: f.required,
    default: f.default,
    placeholder: f.placeholder,
    help: f.help,
    validation: f.validation,
    options: f.options ?? [],
  }));
}

function toPluginType(pluginId: string, d: PluginDescriptor): PluginType {
  return {
    pluginId,
    displayName: d.displayName || d.pluginType,
    canManuallyAdd: d.canManuallyAdd,
    canManuallyDelete: d.canManuallyDelete,
    canAddSymbols: d.canAddSymbols,
    canLockSymbols: d.canLockSymbols,
    connectionForm: toFormFields(d.connectionForm),
    addSymbolForm: toFormFields(d.addSymbolForm),
  };
}

/** Subscribe to the present plugin types; yields the full set per change (snapshot then deltas). */
export async function* subscribePluginTypes(transport: Transport, signal: AbortSignal): AsyncGenerator<PluginType[]> {
  const client = createClient(Plugins, transport);
  const byId = new Map<string, PluginType>();
  for await (const evt of client.subscribe({}, { signal })) {
    if (evt.op === PluginEvent_Op.DELETE) byId.delete(evt.pluginId);
    else if (evt.descriptor) byId.set(evt.pluginId, toPluginType(evt.pluginId, evt.descriptor));
    yield [...byId.values()];
  }
}

// ── manual controller management (Plugins.{Add,Update,Remove}Controller) ──────

export interface FieldErrorItem {
  key: string;
  message: string;
}
export interface AddControllerResult {
  ok: boolean;
  controllerId: string;
  fieldErrors: FieldErrorItem[];
  error: string;
}
export interface MutateResult {
  ok: boolean;
  fieldErrors: FieldErrorItem[];
  error: string;
}

const toFieldErrors = (es: readonly FieldErrorItem[] | undefined): FieldErrorItem[] =>
  (es ?? []).map((e) => ({ key: e.key, message: e.message }));

export async function addController(transport: Transport, pluginId: string, values: Record<string, string>): Promise<AddControllerResult> {
  const reply = await createClient(Plugins, transport).addController({ pluginId, inner: { values } });
  return {
    ok: !resultFailed(reply.result),
    controllerId: reply.controllerId,
    fieldErrors: toFieldErrors(reply.fieldErrors),
    error: resultErrorText(reply.result),
  };
}

export async function updateController(
  transport: Transport,
  pluginId: string,
  controllerId: string,
  values: Record<string, string>,
): Promise<MutateResult> {
  const reply = await createClient(Plugins, transport).updateController({ pluginId, inner: { controllerId, values } });
  return { ok: !resultFailed(reply.result), fieldErrors: toFieldErrors(reply.fieldErrors), error: resultErrorText(reply.result) };
}

export async function removeController(
  transport: Transport,
  pluginId: string,
  controllerId: string,
): Promise<{ ok: boolean; error: string }> {
  const reply = await createClient(Plugins, transport).removeController({ pluginId, inner: { controllerId } });
  return { ok: !resultFailed(reply.result), error: resultErrorText(reply.result) };
}

// ── manual symbol management (Plugins.{Add,Delete}Symbols) ────────────────────

export interface AddSymbolResultItem {
  symbolId: string;
  ok: boolean;
  fieldErrors: FieldErrorItem[];
  error: string;
}
export interface DeleteSymbolResultItem {
  symbolId: string;
  ok: boolean;
  errorCode: string;
  error: string;
}

export async function addSymbols(
  transport: Transport,
  pluginId: string,
  controllerId: string,
  entries: Record<string, string>[],
): Promise<{ ok: boolean; results: AddSymbolResultItem[]; error: string }> {
  const reply = await createClient(Plugins, transport).addSymbols({
    pluginId,
    inner: { controllerId, symbols: entries.map((values) => ({ values })) },
  });
  return {
    ok: !resultFailed(reply.result),
    results: (reply.results ?? []).map((r) => ({
      symbolId: r.symbolId,
      ok: r.ok,
      fieldErrors: toFieldErrors(r.fieldErrors),
      error: r.error,
    })),
    error: resultErrorText(reply.result),
  };
}

export async function deleteSymbols(
  transport: Transport,
  pluginId: string,
  controllerId: string,
  symbolIds: string[],
): Promise<{ ok: boolean; results: DeleteSymbolResultItem[]; error: string }> {
  // symbol_id on cmd RPCs is the SUBJECT-ENCODED form, never the decoded path —
  // the grid passes decoded paths, so encode them like every other symbol RPC
  // (setPollingPriority/lockSymbols/writeSymbols/addSymbols all do). Without this
  // any symbol with a dot/bracket/special char fails the plugin's deletable gate.
  const reply = await createClient(Plugins, transport).deleteSymbols({
    pluginId,
    inner: { controllerId, symbolIds: symbolIds.map(encodeSymbolPath) },
  });
  return {
    ok: !resultFailed(reply.result),
    results: (reply.results ?? []).map((r) => ({ symbolId: r.symbolId, ok: r.ok, errorCode: r.errorCode, error: r.error })),
    error: resultErrorText(reply.result),
  };
}

// ── bulk executor — bounded-concurrency chunked unary (scales to 500k+) ───────

export interface BulkProgress {
  done: number;
  total: number;
  ok: number;
  failed: number;
}
export interface BulkOutcome {
  total: number;
  ok: number;
  failed: number;
  failures: SymbolOpResult[];
}

/**
 * Run a symbol operation over a large path list with constant memory.
 *
 * Browsers can't client-stream over Connect, so a 500k op is split into bounded
 * unary calls fired with bounded concurrency. We slice `paths` lazily — only
 * `concurrency × batchSize` heavy request objects ever exist at once, never the
 * whole set. `runBatch` does one RPC (writeSymbols / setSymbolsLocked / …) and
 * returns its per-symbol results; failures are aggregated (capped so 500k
 * failures can't blow memory) and progress is streamed back.
 */
export async function runChunkedSymbolOp(
  paths: string[],
  runBatch: (batch: string[]) => Promise<SymbolOpResult[]>,
  opts?: { batchSize?: number; concurrency?: number; maxFailuresKept?: number; onProgress?: (p: BulkProgress) => void },
): Promise<BulkOutcome> {
  const batchSize = Math.max(1, Math.min(opts?.batchSize ?? 1000, 1000)); // api-gateway cap
  const concurrency = Math.max(1, opts?.concurrency ?? 8);
  const maxFailuresKept = opts?.maxFailuresKept ?? 1000;
  const total = paths.length;
  const out: BulkOutcome = { total, ok: 0, failed: 0, failures: [] };
  let cursor = 0; // next slice start — shared across the worker pool

  const worker = async (): Promise<void> => {
    for (;;) {
      const start = cursor;
      if (start >= total) return;
      cursor = start + batchSize;
      const batch = paths.slice(start, Math.min(start + batchSize, total));
      let results: SymbolOpResult[];
      try {
        results = await runBatch(batch);
      } catch (e) {
        // Whole-batch transport failure → every path in it counts as failed.
        const msg = e instanceof Error ? e.message : String(e);
        results = batch.map((p) => ({ symbolId: p, ok: false, error: msg, errorCode: 'RPC_FAILED' }));
      }
      for (const r of results) {
        if (r.ok) out.ok++;
        else {
          out.failed++;
          if (out.failures.length < maxFailuresKept) out.failures.push(r);
        }
      }
      opts?.onProgress?.({ done: out.ok + out.failed, total, ok: out.ok, failed: out.failed });
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, Math.ceil(total / batchSize)) }, () => worker()));
  return out;
}

// ── user data: symbol labels (UserData service) ──────────────────────────────
// Labels persist in the `user_data` KV bucket (api-gateway is the only writer),
// survive controller lifecycle, and the indexer overlays them onto SymbolRow +
// makes them FTS-searchable. The UI passes DECODED paths; we encode to the
// subject-id form on the way out and decode on the way back (so callers never
// touch pathenc).

/** Write one symbol's label. `label: ''` deletes it. Persists immediately. */
export async function putLabel(transport: Transport, controllerId: string, path: string, label: string): Promise<void> {
  const client = createClient(UserData, transport);
  // ONE plural verb — a single label is a 1-element batch (no singular PutLabel).
  await client.putLabels({ edits: [{ controllerId, symbolId: encodeSymbolPath(path), label }] });
}

/** One label assignment for a bulk write. `label: ''` deletes. */
export interface LabelEditInput {
  controllerId: string;
  /** Decoded path (matches CatalogRow.path). */
  path: string;
  label: string;
}

/** Result of a bulk write — counts + the decoded paths that still failed. */
export interface PutLabelsResult {
  written: number;
  failed: number;
  failedPaths: string[];
}

/**
 * Bulk-write many labels in ONE RPC — the AI mass-labeling path. Thousands of
 * arbitrary-length labels in a single call; api-gateway writes them with
 * bounded concurrency and reports any that failed (resubmit just those).
 */
export async function putLabels(transport: Transport, edits: LabelEditInput[]): Promise<PutLabelsResult> {
  const client = createClient(UserData, transport);
  const reply = await client.putLabels({
    edits: edits.map((e) => ({
      controllerId: e.controllerId,
      symbolId: encodeSymbolPath(e.path),
      label: e.label,
    })),
  });
  return {
    written: reply.written,
    failed: reply.failed,
    failedPaths: reply.failedSymbolIds.map(decodeSymbolPath),
  };
}

/** A decoded label change from SubscribeLabels. */
export interface LabelChange {
  controllerId: string;
  /** Decoded path. */
  path: string;
  /** Label text; '' when `deleted`. */
  label: string;
  deleted: boolean;
}

/**
 * Subscribe to the gateway's ENTIRE labeled set. The first frames are the
 * snapshot — and because only labeled symbols exist as KV keys, that snapshot
 * is just the (small) labeled set, NOT a scan of the whole catalog. Subsequent
 * frames are live edits. A manager holds these in a Map so "which symbols have
 * labels" + the label filter are instant and the AI can read every label.
 */
export async function* subscribeLabels(transport: Transport, signal: AbortSignal): AsyncGenerator<LabelChange> {
  const client = createClient(UserData, transport);
  for await (const evt of client.subscribeLabels({}, { signal })) {
    yield {
      controllerId: evt.controllerId,
      path: decodeSymbolPath(evt.symbolId),
      label: evt.label,
      deleted: evt.op === LabelEvent_Op.DELETE,
    };
  }
}

// ── user data: controller descriptions (UserData service) ────────────────────
// A free-text note per controller, in the same user_data bucket as labels. The
// UI shows it as the controller subtitle (with a generic fallback when unset).

/** Write a controller's description. `description: ''` deletes it. Persists immediately. */
export async function putControllerDescription(transport: Transport, controllerId: string, description: string): Promise<void> {
  const client = createClient(UserData, transport);
  await client.putControllerDescription({ controllerId, description });
}

/** A decoded controller-description change from SubscribeControllerDescriptions. */
export interface ControllerDescriptionChange {
  controllerId: string;
  /** Description text; '' when `deleted`. */
  description: string;
  deleted: boolean;
}

/**
 * Subscribe to the gateway's controller descriptions — snapshot (one per
 * described controller, so tiny) then live deltas. The UI holds these in a small
 * map and renders the current controller's description live.
 */
export async function* subscribeControllerDescriptions(
  transport: Transport,
  signal: AbortSignal,
): AsyncGenerator<ControllerDescriptionChange> {
  const client = createClient(UserData, transport);
  for await (const evt of client.subscribeControllerDescriptions({}, { signal })) {
    yield {
      controllerId: evt.controllerId,
      description: evt.description,
      deleted: evt.op === ControllerDescriptionEvent_Op.DELETE,
    };
  }
}

/** Write the gateway's OWN description. `description: ''` deletes it. */
export async function putGatewayDescription(transport: Transport, description: string): Promise<void> {
  const client = createClient(UserData, transport);
  await client.putGatewayDescription({ description });
}

/** Subscribe to the gateway's single description — current value then deltas. */
export async function* subscribeGatewayDescription(
  transport: Transport,
  signal: AbortSignal,
): AsyncGenerator<{ description: string; deleted: boolean }> {
  const client = createClient(UserData, transport);
  for await (const evt of client.subscribeGatewayDescription({}, { signal })) {
    yield { description: evt.description, deleted: evt.op === GatewayDescriptionEvent_Op.DELETE };
  }
}

// ── user data: saved symbol combinations (UserData service) ──────────────────
// Named sets of 1..3 History-tab symbol paths, org-shared, stored one KV entry
// per combo. Unary CRUD only — the manage dialog lists on open; no subscribe.

/** One saved symbol combination, decoded for the UI. */
export interface SymbolCombo {
  /** Client-generated uuid; putting an existing id overwrites (edit). */
  id: string;
  /** Human-readable name, non-empty. */
  name: string;
  /** Decoded symbol paths, 1..3 — the History tab's lane set. */
  symbolPaths: string[];
  /** Server-stamped write time (ms since epoch). */
  updatedAtMs: number;
}

/** List a controller's saved combos, sorted by name (server-side). */
export async function listSymbolCombos(
  transport: Transport,
  controllerId: string,
): Promise<SymbolCombo[]> {
  const client = createClient(UserData, transport);
  const reply = await client.listSymbolCombos({ controllerId });
  return reply.combos.map((c) => ({
    id: c.id,
    name: c.name,
    symbolPaths: [...c.symbolPaths],
    updatedAtMs: Number(c.updatedAtMs),
  }));
}

/** Create or overwrite one combo (keyed by combo.id). Persists immediately. */
export async function putSymbolCombo(
  transport: Transport,
  controllerId: string,
  combo: { id: string; name: string; symbolPaths: string[] },
): Promise<void> {
  const client = createClient(UserData, transport);
  await client.putSymbolCombo({ controllerId, combo: { ...combo, updatedAtMs: 0n } });
}

/** Delete one combo. Idempotent — deleting an absent combo succeeds. */
export async function deleteSymbolCombo(
  transport: Transport,
  controllerId: string,
  comboId: string,
): Promise<void> {
  const client = createClient(UserData, transport);
  await client.deleteSymbolCombo({ controllerId, comboId });
}

/** One historical numeric sample, decoded — ready to seed a chart buffer. */
export interface HistorySample {
  /** Decoded path (matches CatalogRow.path / buffer key). */
  symbol: string;
  /** Observed time, ms since epoch (truncated from the historian's ns). */
  tsMs: number;
  /** Numeric projection (bool→0/1). Non-numeric samples are dropped. */
  num: number;
}

/**
 * Fetch the last `windowMs` of history for a SET of symbols in ONE round-trip
 * (the chart-buffer seed). Piggybacked on each viewport-window shift — NOT
 * per-scroll — so the cost is one historian query per window, not per row. The
 * historian (edge Parquet TSDB) scans only the requested symbol-set over the
 * range, so a 30 s window is mostly the in-RAM active buffer. Returns numeric
 * samples keyed by DECODED path; symbols with no recent change return nothing
 * (their buffer keeps the live seed). Bool/string samples are dropped (charts
 * are numeric).
 */
// api-gateway caps a single queryRange at this many symbols (DoS guard). A full
// IndexerPagedView window stays under it, so the common case is ONE round-trip;
// the chunking below is just defensive insurance for an unexpectedly large set.
const HISTORY_SYMBOL_CHUNK = 2000;

export async function queryRange(
  transport: Transport,
  controllerId: string,
  symbolPaths: string[],
  t1Ms: number,
  t2Ms: number,
  limit = 200_000,
  signal?: AbortSignal,
): Promise<HistorySample[]> {
  if (symbolPaths.length === 0) return [];
  const client = createClient(Historian, transport);
  const t1Ns = BigInt(Math.floor(t1Ms)) * 1_000_000n;
  const t2Ns = BigInt(Math.floor(t2Ms)) * 1_000_000n;
  // Scope decides the symbol dialect. CONTROLLER scope stores encoded subject
  // tails and this API speaks decoded display paths → encode on send, decode on
  // receive. MODEL scope ('model:<mid>') stores plain variable ids VERBATIM
  // (subject values.<gw>.models.<mid>.variables.<vid>; the historian's parse
  // test pins sid = current_speed). Blanket-encoding model vids turned
  // stage_num into stage__num — a symbol the historian never stored — so every
  // underscore-named variable seeded zero rows, silently.
  const isModelScope = controllerId.startsWith('model:');
  const out: HistorySample[] = [];
  // Decode each distinct symbol ONCE per call. A raw History-tab page is ~10k
  // rows of one symbol, and decodeSymbolPath walks the token char by char —
  // doing that per row decoded the same string 10k times.
  const decoded = new Map<string, string>();
  const displayPath = (sym: string): string => {
    if (isModelScope) return sym;
    let d = decoded.get(sym);
    if (d === undefined) {
      d = decodeSymbolPath(sym);
      decoded.set(sym, d);
    }
    return d;
  };
  // Follow the pagination cursor for SINGLE-symbol requests: the historian
  // truncates any reply that would bust the broker's 1 MiB max_payload (a raw
  // fetch on a ~30Hz signal gets there in a ~7-minute window) and sets
  // next_t1_ns for the remainder. Untruncated replies return the cursor as 0
  // — one round-trip, exactly as before. Multi-symbol requests don't follow
  // it (rows sort (symbol, ts), so a cut lands mid-symbol and the cursor is
  // documented unsound there); they keep the truncated best-effort reply.
  // The page cap is a runaway guard, not a limit any sane window reaches.
  const MAX_CURSOR_PAGES = 20;
  for (let i = 0; i < symbolPaths.length; i += HISTORY_SYMBOL_CHUNK) {
    const chunk = symbolPaths.slice(i, i + HISTORY_SYMBOL_CHUNK);
    const symbols = isModelScope ? chunk : chunk.map((p) => encodeSymbolPath(p));
    let curT1Ns = t1Ns;
    for (let page = 0; page < MAX_CURSOR_PAGES; page++) {
      const reply = await client.queryRange(
        {
          symbols,
          controllerId,
          t1Ns: curT1Ns,
          t2Ns,
          limit,
        },
        { signal },
      );
      for (const row of reply.rows) {
        const num = valueNum(row.value);
        if (num === null) continue; // non-numeric → not chartable
        out.push({ symbol: displayPath(row.symbol), tsMs: Number(row.tsNs / 1_000_000n), num });
      }
      if (chunk.length !== 1 || reply.nextT1Ns === 0n) break;
      curT1Ns = reply.nextT1Ns;
    }
  }
  return out;
}

/** Rows per page for the typed reader. The historian can only return what fits
 *  one broker payload (~11k raw rows under its 900 KB reply cap) and pages the
 *  rest via next_t1_ns; asking for more just makes it materialize rows it will
 *  not ship. Same figure the History tab's raw path settled on. */
const TYPED_PAGE_ROWS = 10_000;
/** Pages per symbol before the reader gives up and reports the symbol as
 *  truncated. 100 × 10k = 1M rows, the replay's whole-run row budget — a
 *  runaway guard, not a window any sane replay reaches. */
const TYPED_MAX_PAGES = 100;
/** Symbols walked at once, so a model reading a dozen symbols does not wait
 *  out each walk's round-trips one after another. Four keeps a search under the browser's per-origin connection limit (six) with
 *  room for the History tab beside it, and well inside the historian's 24
 *  admission slots. */
const TYPED_SYMBOLS_IN_FLIGHT = 4;

/**
 * Typed history for replay — see history-typed.ts for why this exists beside
 * queryRange. Differences that matter:
 *
 *  - ONE SYMBOL PER REQUEST, so the cursor is sound and every symbol's window
 *    is walked to the end (or reported in `truncated`). A few symbols walk at
 *    once (TYPED_SYMBOLS_IN_FLIGHT); each walk's pages stay in order.
 *  - The value's TYPE and QUALITY survive; a string is a string, a bool is a bool.
 *  - The backend's non-routine notes — today the api-gateway's retention
 *    narrowing — are RETURNED, never swallowed.
 *  - THROWS on a failed Result. A replay must not mistake "the historian
 *    refused" for "nothing moved in that hour".
 */
export async function queryRangeTyped(
  transport: Transport,
  controllerId: string,
  symbolPaths: string[],
  t1Ms: number,
  t2Ms: number,
  signal?: AbortSignal,
  pageRows = TYPED_PAGE_ROWS,
): Promise<TypedHistory> {
  const notes: string[] = [];
  const truncated: string[] = [];
  if (symbolPaths.length === 0) return { samples: [], notes, truncated };
  const client = createClient(Historian, transport);
  const t1Ns = BigInt(Math.floor(t1Ms)) * 1_000_000n;
  const t2Ns = BigInt(Math.floor(t2Ms)) * 1_000_000n;
  // Same dialect split as queryRange above: controller scope stores ENCODED
  // subject tails, model scope stores variable ids VERBATIM.
  const isModelScope = controllerId.startsWith('model:');

  const walk = async (path: string, walkSignal: AbortSignal): Promise<TypedSample[]> => {
    const wire = isModelScope ? path : encodeSymbolPath(path);
    const stream: TypedSample[] = [];
    let curT1Ns = t1Ns;
    let exhausted = false;
    for (let page = 0; page < TYPED_MAX_PAGES; page++) {
      const reply = await client.queryRange(
        { symbols: [wire], controllerId, t1Ns: curT1Ns, t2Ns, limit: pageRows },
        { signal: walkSignal },
      );
      if (resultFailed(reply.result)) throw new Error(resultErrorText(reply.result) || 'history query failed');
      const summary = reply.result?.summary ?? '';
      if (summary && !isRoutineHistorianSummary(summary) && !notes.includes(summary)) notes.push(summary);
      // A single-symbol request only ever returns that symbol, so the row is
      // keyed by the path the caller asked with — no per-row decode at all.
      for (const row of reply.rows) {
        stream.push({ symbol: path, tsNs: row.tsNs, value: valueTyped(row.value), quality: row.value?.quality ?? 0 });
      }
      if (reply.nextT1Ns === 0n) {
        exhausted = true;
        break;
      }
      curT1Ns = reply.nextT1Ns;
    }
    if (!exhausted) truncated.push(path);
    return stream;
  };

  // Results land in symbol order whatever order the walks finish in, so the
  // merge's tie-break — stream order — is the caller's symbol order still. The
  // walks share one abort (see inFlightLimit): the caller's signal reaches all
  // of them, and one failed walk stops the rest.
  const streams = await inFlightLimit(symbolPaths, TYPED_SYMBOLS_IN_FLIGHT, (path, _i, walkSignal) => walk(path, walkSignal), signal);
  return { samples: mergeByTime(streams), notes, truncated };
}

/** Pages of `queryRangeColumns` in flight at once, across every read in this
 *  worker, taken in turn (`permitPool`). Four for the reasons
 *  TYPED_SYMBOLS_IN_FLIGHT gives: the browser's six connections per origin and
 *  the historian's admission slots are shared with everything else. */
const COLUMN_PAGES_IN_FLIGHT = 4;
const columnPagePermits = permitPool(COLUMN_PAGES_IN_FLIGHT);

/**
 * One symbol's raw history as columns, for the data sandbox — see
 * history-columns.ts for why it exists beside queryRange and queryRangeTyped.
 *
 * The same request the typed reader makes (one symbol per request, so the
 * cursor is sound; types and quality kept; a failed Result throws), walked by
 * `walkColumns` into typed arrays instead of objects, and stopped at `maxRows`
 * with the truncation and the covered window reported. A sibling rather than a
 * change to `queryRangeTyped`, whose callers (statechart replay, simulation)
 * depend on its exact behaviour; the page loop is small enough that the two
 * staying separate costs less than coupling them.
 */
export async function queryRangeColumns(
  transport: Transport,
  controllerId: string,
  symbolPath: string,
  t1Ms: number,
  t2Ms: number,
  maxRows: number,
  signal?: AbortSignal,
  deadlineMs?: number,
): Promise<HistoryColumns> {
  const client = createClient(Historian, transport);
  const t1Ns = BigInt(Math.floor(t1Ms)) * 1_000_000n;
  const t2Ns = BigInt(Math.floor(t2Ms)) * 1_000_000n;
  // Same dialect split as the other readers: controller scope stores ENCODED
  // subject tails, model scope stores variable ids VERBATIM.
  const wire = controllerId.startsWith('model:') ? symbolPath : encodeSymbolPath(symbolPath);
  return walkColumns(
    async (pageT1Ns, limit) => {
      const reply = await client.queryRange({ symbols: [wire], controllerId, t1Ns: pageT1Ns, t2Ns, limit }, { signal });
      if (resultFailed(reply.result)) throw new Error(resultErrorText(reply.result) || 'history query failed');
      return {
        rows: reply.rows.map((row) => ({ tsNs: row.tsNs, value: valueTyped(row.value), quality: row.value?.quality ?? 0 })),
        nextT1Ns: reply.nextT1Ns,
        summary: reply.result?.summary ?? '',
      };
    },
    { t1Ns, t2Ns, maxRows, pageRows: TYPED_PAGE_ROWS, deadlineMs, permit: columnPagePermits },
  );
}

export { AggKind };

/** The earliest/latest recorded instants for a scope, decoded. */
export interface HistoryBounds {
  /** False when nothing is recorded for the scope — min/max are then 0. */
  hasData: boolean;
  /** Earliest recorded sample, ms since epoch (truncated from ns). May be
   *  file-granular on edge (earlier than a filtered scope's true first
   *  sample, never later) and retention-clamped by the gateway. */
  minTsMs: number;
  /** Latest recorded sample, ms since epoch (truncated from ns). */
  maxTsMs: number;
}

/**
 * Fetch the recorded time bounds for a controller (or a symbol subset). The
 * History tab uses this to place its "data begins" boundary when a selected
 * range reaches before the device's first recorded sample.
 */
export async function queryBounds(
  transport: Transport,
  controllerId: string,
  symbolPaths: string[] = [],
  signal?: AbortSignal,
): Promise<HistoryBounds> {
  const client = createClient(Historian, transport);
  const isModelScope = controllerId.startsWith('model:'); // same dialect split as queryRange above
  const reply = await client.queryBounds(
    {
      symbols: isModelScope ? symbolPaths : symbolPaths.map((p) => encodeSymbolPath(p)),
      controllerId,
    },
    { signal },
  );
  return {
    hasData: reply.hasData,
    minTsMs: Number(reply.minTsNs / 1_000_000n),
    maxTsMs: Number(reply.maxTsNs / 1_000_000n),
  };
}

/** One server-aggregated time bucket for a single symbol + agg kind, decoded. */
export interface AggBucketSample {
  /** Bucket START, ms since epoch (truncated from the historian's ns). Buckets
   *  are epoch-aligned to the bucket size (both backends round the row ts down
   *  to a bucket-size multiple), NOT to the query window. */
  tsMs: number;
  value: number;
  sampleCount: number;
}

/**
 * Fetch time-bucketed aggregates for ONE symbol, one agg kind per call. The
 * History tab does not use this directly any more — its min/max/avg band comes
 * from queryEnvelope below in a single round-trip — but SUM/COUNT callers and
 * queryEnvelope's older-historian fallback still do.
 *
 * The reply's effective_bucket_size_seconds is an ECHO of the request. The
 * historian refuses a 0 bucket size (and a window of more than 10k buckets)
 * with a typed INVALID_ARGUMENT result rather than substituting a size the
 * echo could not report (services/historian/internal/query/query.go,
 * aggregatedBucketGuard). So the client never sends 0 — clamped to ≥1 here.
 */
export async function queryAggregated(
  transport: Transport,
  controllerId: string,
  symbolPath: string,
  t1Ms: number,
  t2Ms: number,
  agg: AggKind,
  bucketSizeSeconds: number,
  signal?: AbortSignal,
): Promise<AggBucketSample[]> {
  const client = createClient(Historian, transport);
  const isModelScope = controllerId.startsWith('model:'); // same dialect split as queryRange above
  const reply = await client.queryAggregated(
    {
      symbol: isModelScope ? symbolPath : encodeSymbolPath(symbolPath),
      controllerId,
      t1Ns: BigInt(Math.floor(t1Ms)) * 1_000_000n,
      t2Ns: BigInt(Math.floor(t2Ms)) * 1_000_000n,
      agg,
      bucketSizeSeconds: Math.max(1, Math.round(bucketSizeSeconds)),
    },
    { signal },
  );
  return reply.buckets.map((b) => ({
    tsMs: Number(b.bucketTsNs / 1_000_000n),
    value: b.value,
    sampleCount: Number(b.sampleCount),
  }));
}

/**
 * One joined history bucket — the min/max/avg envelope the History tab renders
 * at aggregated zoom levels. Produced by queryEnvelope below (one
 * AGG_KIND_ENVELOPE round-trip); at the raw zoom level each sample
 * degenerates to min = max = avg with sampleCount 1.
 */
export interface HistoryBucket {
  /** Bucket start (aggregated) or sample time (raw), ms since epoch. */
  tsMs: number;
  min: number;
  max: number;
  avg: number;
  sampleCount: number;
}

/**
 * Fetch the min/max/avg envelope for ONE symbol in ONE round-trip — the
 * History tab's aggregated resolution. Asks for AGG_KIND_ENVELOPE, which the
 * historian answers from the single scan it already runs for any one kind;
 * the old shape was three parallel single-kind RPCs, each paying the gateway
 * JWT verify, the NATS hop, the marshal and a cache entry for the same rows.
 *
 * Falls back to those three calls when a non-empty reply carries no envelope:
 * that is how a historian predating ENVELOPE answers (its default branch puts
 * a count in `value`), and the sub-message's PRESENCE is the only reliable
 * tell — a min of 0 is a legitimate value.
 */
export async function queryEnvelope(
  transport: Transport,
  controllerId: string,
  symbolPath: string,
  t1Ms: number,
  t2Ms: number,
  bucketSizeSeconds: number,
  signal?: AbortSignal,
): Promise<HistoryBucket[]> {
  const client = createClient(Historian, transport);
  const isModelScope = controllerId.startsWith('model:'); // same dialect split as queryRange above
  const reply = await client.queryAggregated(
    {
      symbol: isModelScope ? symbolPath : encodeSymbolPath(symbolPath),
      controllerId,
      t1Ns: BigInt(Math.floor(t1Ms)) * 1_000_000n,
      t2Ns: BigInt(Math.floor(t2Ms)) * 1_000_000n,
      agg: AggKind.ENVELOPE,
      bucketSizeSeconds: Math.max(1, Math.round(bucketSizeSeconds)),
    },
    { signal },
  );
  if (reply.buckets.some((b) => !b.envelope)) {
    return queryEnvelopeAsThreeKinds(transport, controllerId, symbolPath, t1Ms, t2Ms, bucketSizeSeconds, signal);
  }
  const out: HistoryBucket[] = [];
  for (const b of reply.buckets) {
    const e = b.envelope;
    if (!e) continue; // unreachable after the check above; keeps the type honest
    out.push({ tsMs: Number(b.bucketTsNs / 1_000_000n), min: e.min, max: e.max, avg: e.avg, sampleCount: Number(b.sampleCount) });
  }
  return out;
}

/** The pre-ENVELOPE shape, kept only as queryEnvelope's fallback for an older
 *  historian: three single-kind calls joined on bucket ts. All three
 *  aggregate the same rows so the key sets match; the join still tolerates a
 *  mismatch by keying on the union and backfilling a hole from what arrived. */
async function queryEnvelopeAsThreeKinds(
  transport: Transport,
  controllerId: string,
  symbolPath: string,
  t1Ms: number,
  t2Ms: number,
  bucketSizeSeconds: number,
  signal?: AbortSignal,
): Promise<HistoryBucket[]> {
  const [mins, maxs, avgs] = await Promise.all([
    queryAggregated(transport, controllerId, symbolPath, t1Ms, t2Ms, AggKind.MIN, bucketSizeSeconds, signal),
    queryAggregated(transport, controllerId, symbolPath, t1Ms, t2Ms, AggKind.MAX, bucketSizeSeconds, signal),
    queryAggregated(transport, controllerId, symbolPath, t1Ms, t2Ms, AggKind.AVG, bucketSizeSeconds, signal),
  ]);
  const joined = new Map<number, HistoryBucket>();
  const slot = (tsMs: number): HistoryBucket => {
    let b = joined.get(tsMs);
    if (!b) joined.set(tsMs, (b = { tsMs, min: NaN, max: NaN, avg: NaN, sampleCount: 0 }));
    return b;
  };
  for (const b of mins) {
    const j = slot(b.tsMs);
    j.min = b.value;
    j.sampleCount = Math.max(j.sampleCount, b.sampleCount);
  }
  for (const b of maxs) {
    const j = slot(b.tsMs);
    j.max = b.value;
    j.sampleCount = Math.max(j.sampleCount, b.sampleCount);
  }
  for (const b of avgs) {
    const j = slot(b.tsMs);
    j.avg = b.value;
    j.sampleCount = Math.max(j.sampleCount, b.sampleCount);
  }
  const out = [...joined.values()].sort((a, b) => a.tsMs - b.tsMs);
  for (const b of out) {
    const known = [b.min, b.max, b.avg].filter(Number.isFinite);
    if (known.length === 0) continue;
    if (!Number.isFinite(b.min)) b.min = Math.min(...known);
    if (!Number.isFinite(b.max)) b.max = Math.max(...known);
    if (!Number.isFinite(b.avg)) b.avg = (b.min + b.max) / 2;
  }
  return out.filter((b) => Number.isFinite(b.min));
}

/** A live value, decoded + typed — ready to bind to a catalog row and chart. */
export interface TypedValue {
  /** Decoded path (matches CatalogRow.path) — bound from the encoded value sid. */
  symbolId: string;
  /** Typed display value: boolean | number | string, or null (raw/unknown). */
  value: boolean | number | string | null;
  /** Chartable numeric projection (bool→0/1), or null for non-numeric. */
  num: number | null;
  /**
   * The value's OWN last-change time (ms), as stamped by the producer
   * (SymbolValue.timestamp_ns). This is BACKEND-clock time, not browser time —
   * the chart frames everything in the backend clock via ClockSync. It's the
   * linchpin of the carry-forward rule: publish-on-change means the value held
   * constant from this instant until now, so a symbol whose last change predates
   * the chart window has provably been flat the whole window. 0 if absent.
   */
  tsMs: number;
}

function valueTyped(v?: SymbolValue): boolean | number | string | null {
  if (!v) return null;
  switch (v.value.case) {
    case 'boolVal':
      return v.value.value;
    case 'intVal':
      return Number(v.value.value);
    case 'realVal':
      return v.value.value;
    case 'stringVal':
      return v.value.value;
    default:
      return null;
  }
}

function valueNum(v?: SymbolValue): number | null {
  if (!v) return null;
  switch (v.value.case) {
    case 'boolVal':
      return v.value.value ? 1 : 0;
    case 'intVal':
      return Number(v.value.value);
    case 'realVal':
      return v.value.value;
    default:
      return null;
  }
}

/**
 * Subscribe to a controller's live values, DECODED + TYPED for the grid.
 * Every value subject's sid is the ENCODED symbol id; we decode it to the path
 * the catalog (and the grid) keys by, so the value binds to its row.
 *
 * `symbolPaths` SCOPES the subscription: empty = the whole controller firehose;
 * non-empty = only those symbols (api-gateway opens a filtered KV watch, so the
 * browser only ever receives changes for the symbols it's actually showing).
 * The IndexerPagedView passes its window's chartable leaves and re-subscribes on
 * window shift — so a 10k-symbol controller churning 50k/s only streams the
 * ~window's worth of changes to this client.
 */
export async function* subscribeTypedValues(
  transport: Transport,
  controllerId: string,
  symbolPaths: string[],
  signal: AbortSignal,
  // > 0 → CHART mode: api-gateway serves from the uncompacted value_changes log
  // starting this many ms in the past (full-res seed + live in one stream). 0 →
  // the compacted live_values latest-value cache (the numeric column).
  chartHistoryMs: bigint = 0n,
  // Non-empty → WINDOW-FOLLOW mode: the api-gateway scopes this stream
  // server-side to the leaf rows of the SymbolQuery fetches tagged with the
  // same id (PageQuery.clientSession) and re-scopes it as they change — the
  // view opens ONE stream at T0 and never re-subscribes on window shifts.
  // symbolPaths must be empty (the set comes from the fetches).
  followSession = '',
): AsyncGenerator<TypedValue> {
  const client = createClient(Values, transport);
  const symbolIds = symbolPaths.map((p) => encodeSymbolPath(p));
  // acceptBatched: the server may coalesce burst phases (snapshot/seed — a
  // 1,000-leaf window is ~16 container frames instead of ~1,000 singles).
  // A frame with `batched` non-empty is a carrier; its own fields are unset.
  for await (const frame of client.subscribe(
    { controllerId, symbolIds, sinceRevision: 0n, chartHistoryMs, followSession, acceptBatched: true },
    { signal },
  )) {
    const events = frame.batched?.length ? frame.batched : [frame]; // optional: tolerate a pre-batching schema
    for (const ev of events) {
      if (ev.op === ValueEvent_Op.PUT) {
        const tsMs = ev.value?.timestampNs != null ? Number(ev.value.timestampNs / 1_000_000n) : 0;
        yield { symbolId: decodeSymbolPath(ev.symbolId), value: valueTyped(ev.value), num: valueNum(ev.value), tsMs };
      }
    }
  }
}

/**
 * Live values of an INTEGRATION's observed paths — the same Values.Subscribe
 * stream under integration scope (values.<gw>.integrations.<id>.>). WINDOW-FOLLOW
 * ONLY: symbolIds is always empty — the path set comes from the IntegrationIndex
 * page fetches tagged with the same clientSession (== followSession). The
 * api-gateway scopes the stream to the viewport's leaves AND drives the edge
 * observe-lease, so those paths get produced (an integration value exists only
 * while observed). Same TypedValue shape + decode as subscribeTypedValues;
 * symbolId carries the encoded path.
 */
export async function* subscribeIntegrationTypedValues(
  transport: Transport,
  integrationId: string,
  signal: AbortSignal,
  chartHistoryMs: bigint = 0n,
  followSession = '',
): AsyncGenerator<TypedValue> {
  const client = createClient(Values, transport);
  for await (const frame of client.subscribe(
    { integrationId, symbolIds: [], sinceRevision: 0n, chartHistoryMs, followSession, acceptBatched: true },
    { signal },
  )) {
    const events = frame.batched?.length ? frame.batched : [frame];
    for (const ev of events) {
      if (ev.op === ValueEvent_Op.PUT) {
        const tsMs = ev.value?.timestampNs != null ? Number(ev.value.timestampNs / 1_000_000n) : 0;
        yield { symbolId: decodeSymbolPath(ev.symbolId), value: valueTyped(ev.value), num: valueNum(ev.value), tsMs };
      }
    }
  }
}

/**
 * Live values of a MODEL's published variables — the same Values.Subscribe
 * stream under model scope (values.<gw>.models.<mid>.variables.>). A model's
 * outputs are read exactly like controller data (the composition invariant),
 * so the yielded shape is the same TypedValue; symbolId carries the variable
 * id. Snapshot first (current value of every published variable), then deltas.
 */
/*
 * An INTEGRATION's values for a named set of paths.
 *
 * The grid reads an integration through WINDOW-FOLLOW, where the leaf set comes
 * from tagged page fetches and the api-gateway leases those paths from the edge
 * service — integration values are produced only WHILE OBSERVED, so somebody has
 * to declare the observation. A dashboard does not page: it knows its paths at
 * mount and never scrolls, so it subscribes them explicitly, which the server's
 * non-follow path supports for any scope.
 *
 * Whether the explicit path also LEASES is the open question this makes visible
 * rather than assumes — a tile whose integration value never arrives shows as
 * not reading in the Sources tab, which is the honest report either way.
 */
export async function* subscribeIntegrationValues(
  transport: Transport,
  integrationId: string,
  paths: string[],
  signal: AbortSignal,
): AsyncGenerator<TypedValue> {
  const client = createClient(Values, transport);
  const symbolIds = paths.map((p) => encodeSymbolPath(p));
  for await (const frame of client.subscribe(
    { controllerId: '', symbolIds, sinceRevision: 0n, integrationId, acceptBatched: true },
    { signal },
  )) {
    const events = frame.batched?.length ? frame.batched : [frame];
    for (const ev of events) {
      if (ev.op !== ValueEvent_Op.PUT || !ev.value) continue;
      const tsMs = ev.value.timestampNs != null ? Number(ev.value.timestampNs / 1_000_000n) : 0;
      // DECODED back to the readable path, matching what the browse returns and
      // what a tile's config names — the encode above is only for the subject.
      yield { symbolId: decodeSymbolPath(ev.symbolId), value: valueTyped(ev.value), num: valueNum(ev.value), tsMs };
    }
  }
}

export async function* subscribeModelValues(transport: Transport, modelId: string, signal: AbortSignal): AsyncGenerator<TypedValue> {
  const client = createClient(Values, transport);
  for await (const ev of client.subscribe({ controllerId: '', symbolIds: [], sinceRevision: 0n, modelId }, { signal })) {
    if (ev.op === ValueEvent_Op.PUT) {
      const tsMs = ev.value?.timestampNs != null ? Number(ev.value.timestampNs / 1_000_000n) : 0;
      // The variable id goes through VERBATIM. Model variable ids are NOT
      // encoded symbol paths — they are plain ids used as-is in the subject
      // (values.<gw>.models.<mid>.variables.<vid>; the historian's parse test
      // pins sid = current_speed, verbatim). Running them through
      // decodeSymbolPath turned stage_num into stage.num, so every
      // underscore-named variable missed its row and sat at '—' forever.
      // (An earlier fix ADDED the decode here to match a seed path that was
      // itself wrongly encoding — two wrongs that cancelled only for names
      // with no '_'. queryRange is now scope-aware instead.)
      yield { symbolId: ev.symbolId, value: valueTyped(ev.value), num: valueNum(ev.value), tsMs };
    }
  }
}

/** One IP-safe model log line (producer-sanitized by the model-runner). */
export interface ModelLog {
  tsMs: number;
  severity: string; // "debug" | "info" | "warn" | "error"
  message: string; // the collapsed one-liner (title)
  body: string; // optional expanded detail — the SAME LogEvent.body controllers use ('' when none)
  category: string; // black-box error category attr ('' when none)
}

/** Per-model log tail — bridges Models.SubscribeLogs (recent-window replay then live). */
export async function* subscribeModelLogs(transport: Transport, modelId: string, signal: AbortSignal): AsyncGenerator<ModelLog> {
  const client = createClient(Models, transport);
  for await (const ev of client.subscribeLogs({ modelId }, { signal })) {
    yield {
      tsMs: Number(ev.timestampNs / 1_000_000n),
      severity: ev.severity || 'info',
      message: ev.message,
      body: ev.body,
      category: ev.attrs?.['category'] ?? '',
    };
  }
}

/**
 * One per-controller log line. Plugin-emitted operator status (connect /
 * discovery / program durations, value-snapshot counts, reconnect, shutdown);
 * the plugin keeps protocol secrets + raw PLC dumps on its stderr-only logger
 * and never publishes them. `attrs` carries the structured detail (durations,
 * counts, address, model, firmware, …).
 */
export interface ControllerLog {
  tsMs: number;
  severity: string; // "debug" | "info" | "warn" | "error"
  source: string; // plugin type ("mock" | "s7" | "ads" | …)
  message: string; // the collapsed one-liner (title)
  body: string; // optional expanded detail — free-form text ("" when none)
  attrs: Record<string, string>;
}

/** Per-controller log tail — bridges Controllers.SubscribeLogs (recent-window replay then live). */
export async function* subscribeControllerLogs(
  transport: Transport,
  controllerId: string,
  signal: AbortSignal,
): AsyncGenerator<ControllerLog> {
  const client = createClient(Controllers, transport);
  for await (const ev of client.subscribeLogs({ controllerId }, { signal })) {
    yield {
      tsMs: Number(ev.timestampNs / 1_000_000n),
      severity: ev.severity || 'info',
      source: ev.source,
      message: ev.message,
      body: ev.body,
      attrs: ev.attrs ?? {},
    };
  }
}

// ── live values (Values.Subscribe) ───────────────────────────────────────────
export interface LiveValue {
  symbolId: string;
  text: string;
}

function fmtValue(v?: SymbolValue): string {
  if (!v) return '—';
  switch (v.value.case) {
    case 'boolVal':
      return v.value.value ? 'true' : 'false';
    case 'intVal':
      return v.value.value.toString();
    case 'realVal':
      return v.value.value.toFixed(3);
    case 'stringVal':
      return v.value.value;
    case 'rawVal':
      return `<${v.value.value.length}B>`;
    default:
      return '—';
  }
}

/** Subscribe to a controller's live values (empty symbolIds = all); yields per change. */
export async function* subscribeValues(transport: Transport, controllerId: string, signal: AbortSignal): AsyncGenerator<LiveValue> {
  const client = createClient(Values, transport);
  for await (const ev of client.subscribe({ controllerId, symbolIds: [], sinceRevision: 0n }, { signal })) {
    if (ev.op === ValueEvent_Op.PUT) yield { symbolId: ev.symbolId, text: fmtValue(ev.value) };
  }
}

// ── models (Models.Subscribe / GetModelSource / Update / ExecuteCommand) ─────
//
// The model list + live runtime: the api-gateway streams an OP_PUT per model
// for the DESCRIPTOR and for the RUNTIME (two keys per model), then live deltas.
// We fold both halves into one ModelView per id and re-emit the whole set —
// models are low-cardinality, so a fresh array per change mirrors
// subscribeControllers exactly. The SCXML body is stripped from the stream
// (fetch it via getModelSource); runtime blobs are stripped too.

const KNOWN_MODEL_STATUSES = new Set<ModelStatus>(['starting', 'waiting', 'running', 'paused', 'stopped', 'error']);
function normalizeStatus(s: string): ModelStatus {
  return KNOWN_MODEL_STATUSES.has(s as ModelStatus) ? (s as ModelStatus) : 'unknown';
}
const nsToMs = (ns: bigint): number => Number(ns / 1_000_000n);

// Exported with applyDescriptor for the model-authoring eval (bench/fixtures/model-relay.ts), so
// its fake gateway shows a saved model to the tools as the app does.
export function emptyModelView(id: string): ModelView {
  return {
    id,
    name: id,
    desiredState: 'stopped',
    status: 'unknown',
    currentState: '',
    machines: [],
    error: '',
    startedAtMs: 0,
    elapsedMs: 0,
    sourceRevision: 0,
    description: '',
    commands: [],
    outputs: [],
    outputCount: 0,
    inputs: [],
    hasInterface: false,
    children: [],
  };
}
/** A constant a child's input is bound to (ModelBinding.literal), as the model page shows it. */
function constantText(v: Value | undefined): string {
  switch (v?.v.case) {
    case 'boolVal':
      return v.v.value ? 'true' : 'false';
    case 'intVal':
      return v.v.value.toString();
    case 'realVal':
      return String(v.v.value);
    case 'stringVal':
      return JSON.stringify(v.v.value);
    case 'rawVal':
      return `<${v.v.value.length}B>`;
    default:
      return 'null';
  }
}
/** An input port with the wiring it is given: a path, a constant, or nothing (its parent's to assign), and its declared type. */
function inputPort(name: string, type: string, b: ModelBinding | undefined): ModelChildPort {
  const typed = type ? { type } : {};
  if (b?.source.case === 'path') return { name, path: b.source.value, ...typed };
  if (b?.source.case === 'literal') return { name, path: '', constant: constantText(b.source.value), ...typed };
  return { name, path: '', ...typed };
}
/** An output port, with the PLC path it is wired to and its safe value when it is, and its declared type. */
function outputPort(name: string, type: string, wire: ModelPlcOutput | undefined): ModelChildPort {
  return { name, path: wire?.path ?? '', ...(type ? { type } : {}), ...(wire ? { safe: constantText(wire.safe) } : {}) };
}
/**
 * The input that the platform gives each machine of an array (Step 4): the parent or the wiring
 * starts and stops the machine with it. The template does not declare it. The same name as the
 * host's `ARRAY_INPUTS`. This module does not load the host.
 */
const ENABLED = 'enabled';
/** A group's children with their ports (Part 7 of the hierarchical models plan): each port the
 *  child's template declares, with its type and the wiring the child gives it. An array child
 *  (Step 4) also has its count, and `enabled` when its wiring feeds it. The proto's 0 is a single child. */
function childrenOf(d: ModelDescriptor): ModelChildView[] {
  const names = new Set((d.children ?? []).map((c) => c.name));
  return (d.children ?? []).map((c) => {
    const iface = d.templates?.[c.template]?.modelInterface;
    const inputs = (iface?.inputs ?? []).filter((i) => i.name.length > 0).map((i) => inputPort(i.name, i.type, c.inputs?.[i.name]));
    const outputs = (iface?.outputs ?? []).filter((o) => o.name.length > 0).map((o) => outputPort(o.name, o.type, c.outputs?.[o.name]));
    // A connection: a name the template does not declare, wired to another machine of the group.
    const connections: Record<string, string> = {};
    for (const [name, b] of Object.entries(c.inputs ?? {})) {
      if (b.source.case === 'path' && names.has(b.source.value) && !inputs.some((p) => p.name === name)) connections[name] = b.source.value;
    }
    const named = Object.keys(connections).length > 0 ? { connections } : {};
    if (!c.count) return { name: c.name, template: c.template, inputs, outputs, ...named };
    const enabled = c.inputs?.[ENABLED];
    const wired = enabled?.source.case === 'path' || enabled?.source.case === 'literal';
    // A template that declares `enabled` itself is refused at save. Its port already shows the binding.
    if (wired && !inputs.some((p) => p.name === ENABLED)) inputs.push(inputPort(ENABLED, '', enabled));
    return { name: c.name, template: c.template, count: c.count, inputs, outputs, ...named };
  });
}
/** The root's own wiring (Part 4 of the Step 3 plan), in a child's shape: only the ports it wires. */
function wiringOf(d: ModelDescriptor): { inputs: ModelChildPort[]; outputs: ModelChildPort[] } {
  const typeOf = (name: string, ports: ReadonlyArray<{ name: string; type: string }> | undefined): string => ports?.find((p) => p.name === name)?.type ?? '';
  return {
    inputs: Object.entries(d.inputs ?? {}).map(([name, b]) => inputPort(name, typeOf(name, d.modelInterface?.inputs), b)),
    outputs: Object.entries(d.outputs ?? {}).map(([name, wire]) => outputPort(name, typeOf(name, d.modelInterface?.outputs), wire)),
  };
}
export function applyDescriptor(v: ModelView, d: ModelDescriptor): ModelView {
  return {
    ...v,
    name: d.name || v.id,
    desiredState: d.desiredState || 'stopped',
    description: d.description || d.modelInterface?.description || '',
    commands: (d.modelInterface?.commands ?? []).map((c) => c.name).filter((n) => n.length > 0),
    outputs: (d.modelInterface?.outputs ?? []).map((o) => o.name).filter((n) => n.length > 0),
    outputCount: d.modelInterface?.outputs.length ?? 0,
    inputs: (d.modelInterface?.inputs ?? []).map((i) => i.name).filter((n) => n.length > 0),
    hasInterface: !!d.modelInterface,
    children: childrenOf(d),
    wiring: wiringOf(d),
    // Keep the interface WHOLE — an update must hand it back or the model-runner's
    // verbatim kvPut erases it (see ModelView.iface).
    iface: d.modelInterface
      ? {
          description: d.modelInterface.description ?? '',
          inputs: (d.modelInterface.inputs ?? []).map((i) => ({
            name: i.name,
            type: i.type,
            kind: i.kind,
            optional: i.optional,
            isArray: i.isArray,
          })),
          outputs: (d.modelInterface.outputs ?? []).map((o) => ({ name: o.name, type: o.type, unit: o.unit })),
          commands: (d.modelInterface.commands ?? []).map((c) => ({ name: c.name, params: [...(c.params ?? [])] })),
        }
      : undefined,
  };
}
/** Exported for the tests: a machine of an array keeps `stopped` (Step 4). */
export function applyRuntime(v: ModelView, r: ModelRuntime): ModelView {
  return {
    ...v,
    status: normalizeStatus(r.status),
    currentState: r.currentState,
    machines: (r.machines ?? []).map((m) => ({ path: m.path, state: m.state, fault: m.fault, ...(m.stopped ? { stopped: true } : {}) })),
    error: r.error,
    startedAtMs: nsToMs(r.startedAtNs),
    elapsedMs: nsToMs(r.elapsedNs),
  };
}

/** Subscribe to models. Yields the FULL current set on every change (descriptor
 *  OR runtime), folded per id — mirrors subscribeControllers. */
export async function* subscribeModels(transport: Transport, signal: AbortSignal): AsyncGenerator<ModelView[]> {
  const client = createClient(Models, transport);
  const byId = new Map<string, ModelView>();
  for await (const evt of client.subscribe({}, { signal })) {
    const id = evt.modelId;
    if (evt.op === ModelEvent_Op.DELETE) {
      byId.delete(id);
    } else if (evt.state.case === 'descriptor') {
      // The revision is how a page that holds the SCXML body (not in this stream) learns of a save.
      const view = applyDescriptor(byId.get(id) ?? emptyModelView(id), evt.state.value);
      byId.set(id, { ...view, sourceRevision: Number(evt.revision) });
    } else if (evt.state.case === 'runtime') {
      byId.set(id, applyRuntime(byId.get(id) ?? emptyModelView(id), evt.state.value));
    }
    yield [...byId.values()];
  }
}

export interface ModelSource {
  scxml: string;
  sha256: string;
  /**
   * The rest of the descriptor, from the same read, for an editor (Part 5 of the Step 3 plan): the
   * interface, each template with its text, the children and the root's wiring, with
   * their constants as typed. Its own `scxml` is empty: the root's text is `scxml`. Absent from a
   * relay older than Part 5.
   */
  descriptor?: ModelDescriptor;
}

/** Fetch the full SCXML body on demand (Subscribe omits it), and the rest of the descriptor with it. */
export async function getModelSource(transport: Transport, modelId: string): Promise<ModelSource> {
  const client = createClient(Models, transport);
  const r = await client.getModelSource({ modelId });
  return { scxml: r.scxml, sha256: r.scxmlSha256, ...(r.descriptor ? { descriptor: r.descriptor } : {}) };
}

/** The ONE command surface — 5 reserved lifecycle names (start/stop/pause/
 *  resume/reset) + a model's declared commands. */
export async function executeModelCommand(
  transport: Transport,
  modelId: string,
  command: string,
  eventData?: Record<string, string>,
): Promise<void> {
  const client = createClient(Models, transport);
  await client.executeCommand({ modelId, inner: { eventName: command, eventData: eventData ?? {} } });
}

export interface ModelUpsert {
  name: string;
  scxml: string;
  desiredState: string; // "running" | "stopped"
  /** The model's own description — a top-level descriptor field (works for models
   *  with NO interface). Send with updateMask ['description']. */
  description?: string;
  /** The model's declared interface — send it ONLY when this write means to change
   *  it (i.e. it is in `updateMask`). */
  modelInterface?: ModelIface;
  /** A group's templates, whole, by name: each template's text and interface. With ['templates']. */
  templates?: Record<string, MessageInitShape<typeof ModelTemplateSchema>>;
  /** A group's children, whole, in order, with their wiring. With ['children']. */
  children?: MessageInitShape<typeof ModelChildSchema>[];
  /** The root's own wiring (Part 4 of the Step 3 plan): each input's path or constant. With ['inputs']. */
  inputs?: Record<string, MessageInitShape<typeof ModelBindingSchema>>;
  /** The root's PLC outputs, each with its safe value. With ['outputs']. */
  outputs?: Record<string, MessageInitShape<typeof ModelPlcOutputSchema>>;
  /** Who gets a Slack message at each state change. With ['notify']; left out with it, nobody does. */
  notify?: MessageInitShape<typeof ModelNotifySchema>;
  /** WHICH descriptor fields this write is setting. Required.
   *
   *  The model-runner OWNS the descriptor: it merges only the named fields into
   *  what it holds, so a caller cannot erase a field it didn't name — the binding
   *  editor sends ["inputs"] and structurally cannot touch the interface or the
   *  SCXML. Consolidation is the owner's job; this is the frontend saying what it
   *  MEANS, not reconstructing state it doesn't own.
   *
   *  ["*"] deliberately replaces the WHOLE descriptor — an authoring rewrite of a model that
 *  already exists. It is NOT how a model is brought into being: see `createModel`. */
  updateMask: string[];
}

/**
 * Create a model that does not exist yet. Gated on `model:create`, not `model:edit`.
 *
 * SEPARATE FROM update BECAUSE THE PERMISSIONS ARE. While ["*"] on an unknown id was the way to
 * create, `model:edit` was enough to bring a model into existence and `model:create` gated nothing
 * in its own domain — the permission checked at the front door said "edit" and the write performed
 * said "create". The model-runner closed that (descriptorMerge refuses a replace-all with no
 * existing descriptor) and this side never followed, so creating stopped working entirely: the
 * refusal names `model:create`, so the caller concluded it lacked a permission it actually holds
 * and went looking at roles for a verb that was simply never called.
 *
 * The whole descriptor, no mask — there is nothing to merge into.
 */
export async function createModel(transport: Transport, modelId: string, m: ModelUpsert): Promise<void> {
  const client = createClient(Models, transport);
  const reply = await client.create({
    modelId,
    descriptor: {
      name: m.name,
      scxml: m.scxml,
      desiredState: m.desiredState,
      description: m.description ?? '',
      ...(m.modelInterface ? { modelInterface: m.modelInterface } : {}),
      ...groupParts(m),
    },
  });
  // Same reason as update: a refusal rides IN the Result, and dropping it would let a rejected
  // create resolve exactly like an accepted one.
  if (resultErrorText(reply.result)) throw new Error(resultErrorText(reply.result));
}

/** The parts of a group a write carries, the root's wiring and the notify: only those it names. */
function groupParts(m: ModelUpsert): Pick<ModelUpsert, 'templates' | 'children' | 'inputs' | 'outputs' | 'notify'> {
  return {
    ...(m.templates ? { templates: m.templates } : {}),
    ...(m.children ? { children: m.children } : {}),
    ...(m.inputs ? { inputs: m.inputs } : {}),
    ...(m.outputs ? { outputs: m.outputs } : {}),
    ...(m.notify ? { notify: m.notify } : {}),
  };
}

/** Edit a model that exists — deltas by mask, or ["*"] for an authoring rewrite. Forwarded by
 *  api-gateway to the model-runner's cmd.…apply (api-gateway writes no state). */
export async function updateModel(transport: Transport, modelId: string, m: ModelUpsert): Promise<void> {
  const client = createClient(Models, transport);
  const reply = await client.update({
    modelId,
    inner: {
      descriptor: {
        name: m.name,
        scxml: m.scxml,
        desiredState: m.desiredState,
        description: m.description ?? '',
        ...(m.modelInterface ? { modelInterface: m.modelInterface } : {}),
        ...groupParts(m),
      },
      updateMask: m.updateMask,
    },
  });
  /*
   * THE REFUSAL WAS BEING THROWN AWAY, and this is the one verb where that costs the most.
   *
   * A refusal on this platform rides IN the Result — a 200-shaped reply — precisely so a caller
   * gets a sentence rather than a transport error. The model-runner pre-parses every write and
   * answers `invalid SCXML: <reason>` for one it cannot run. This function discarded the reply, so
   * a rejected save resolved exactly like an accepted one: the editor cleared its spinner and the
   * model on the box was unchanged.
   *
   * Every other reply in this file is checked this way. This one was not.
   */
  if (resultErrorText(reply.result)) throw new Error(resultErrorText(reply.result));
}

/**
 * Delete a model. Forwarded to the model-runner, which drops the descriptor it owns; its own KV
 * watch stops the interpreter and cleans up the runtime and variable entries.
 *
 * Gated by `model:delete`, which is its own permission rather than `model:edit` — editing a
 * statechart and removing it are different powers.
 */
export async function deleteModel(transport: Transport, modelId: string): Promise<void> {
  const client = createClient(Models, transport);
  const reply = await client.delete({ modelId, inner: {} });
  // The refusal rides IN the Result, as everywhere else on this plane.
  if (resultErrorText(reply.result)) throw new Error(resultErrorText(reply.result));
}

// ── entitlements: the org's plan (SessionEntitlements.Subscribe) ──────────────
//
// ORG-scoped, not gateway-scoped, so it rides the cloud transport: one
// subscription per session covers every relay the org owns.
//
// The frame carries EVERY capability in the catalog, including the ones the org
// does NOT have — a UI needs the absent entries to say "locked, because your plan
// is X" rather than inferring a lock from missing data, which is what causes
// layout shift on a stream that is quiet most of the time.

/** The org's plan, decoded into the shape base-ui's Session consumes. */
export interface SessionPlanView {
  orgId: string;
  gatewayId: string;
  capabilities: {
    feature: string;
    active: boolean;
    lifecycle: 'unspecified' | 'active' | 'grace' | 'expired';
    limits: Record<string, number>;
    graceUntilMs: number;
    choice?: string;
    members?: string[];
  }[];
  /** The caller's authored permission matrix, when the frame carried it.
   *  `undefined` is NOT an empty matrix: it means the store could not say, and
   *  the consumer keeps whatever it last had (base-ui setAuthoredGrants). */
  authored?: Record<string, boolean>;
}

const LIFECYCLE: Record<number, SessionPlanView['capabilities'][number]['lifecycle']> = {
  0: 'unspecified',
  1: 'active',
  2: 'grace',
  3: 'expired',
};

export async function* subscribeSessionPlan(transport: Transport, gatewayId: string, signal: AbortSignal): AsyncGenerator<SessionPlanView> {
  const client = createClient(SessionEntitlements, transport);
  for await (const p of client.subscribe({ gatewayId }, { signal })) {
    yield {
      orgId: p.orgId,
      gatewayId: p.gatewayId,
      capabilities: p.features.map((f) => ({
        feature: f.feature,
        active: f.active,
        lifecycle: LIFECYCLE[f.lifecycle] ?? 'unspecified',
        limits: Object.fromEntries(Object.entries(f.limits ?? {}).map(([k, v]) => [k, Number(v)])),
        graceUntilMs: Number(f.graceUntilMs ?? 0n),
        choice: f.choice || undefined,
        members: f.members?.length ? [...f.members] : undefined,
      })),
      // Presence is the contract: absent stays absent (see SessionPlanView).
      authored: p.authored ? { ...p.authored.grants } : undefined,
    };
  }
}

// ── fleet: quick-claim (FleetControl.DiscoverNearby / Claim) ──────────────────
// The cloud control-plane. These ALL run on the CLOUD transport (makeCloudTransport):
// FleetControl is cloud-scoped (cross-org claim registry), never edge-scoped. The
// server-only record fields (last_seen_net_hash, device_public_key, claim_code_hash)
// are not part of the decoded domain type — the UI never needs them.

/** A decoded fleet gateway record — the shape the fleet UI consumes (no
 *  protobuf, bigint, or byte fields leak past this boundary). */
export interface FleetGateway {
  /** The permanent device-key fingerprint — the box identity (claim-stable). */
  deviceFingerprint: string;
  /** Owning org (org id); '' = enrolled-but-unclaimed. */
  orgId: string;
  /** User-set label ('' if none). */
  displayName: string;
  /** Live presence from the leaf connect/disconnect. */
  status: 'online' | 'offline' | 'unknown';
  /** Self-reported advisory metadata (never trusted for auth). */
  softwareVersion: string;
  hostname: string;
  /** Coarse geo, derived cloud-side. */
  geo: string;
  /** 'edge' | 'cloud' | ''. */
  nodeKind: string;
  /** REAL host-NIC IPs (IT / OT / WiFi), read cloud-side from the box's
   *  NetworkState BY ROLE — '' when the box has no such NIC or hasn't reported.
   *  Drive the gateway IP badges AND the LAN-vs-cloud race legs (it/ot/wifi.<fp>). */
  itIp: string;
  otIp: string;
  wifiIp: string;
  /** Cloud-observed source IP of the box — org-scoped (only the owning org sees
   *  it). '' if unknown. Drives the public-IP badge. */
  publicIp: string;
  /** First/last seen, ms since epoch (truncated from the registry's ns). */
  firstSeenMs: number;
  lastSeenMs: number;
  /** GeoIP coordinates resolved cloud-side from the public IP; 0/0 =
   *  unresolved. Drives the dashboard map's fleet pins. */
  lat: number;
  lng: number;
}

const FLEET_STATUS: Record<GatewayStatus, FleetGateway['status']> = {
  [GatewayStatus.ONLINE]: 'online',
  [GatewayStatus.OFFLINE]: 'offline',
  [GatewayStatus.UNSPECIFIED]: 'unknown',
};

function toFleetGateway(r: GatewayRecord): FleetGateway {
  return {
    deviceFingerprint: r.deviceFingerprint,
    orgId: r.orgId,
    displayName: r.displayName,
    status: FLEET_STATUS[r.status] ?? 'unknown',
    softwareVersion: r.metadata?.softwareVersion ?? '',
    hostname: r.metadata?.hostname ?? '',
    geo: r.metadata?.geo ?? '',
    nodeKind: r.metadata?.nodeKind ?? '',
    itIp: r.network?.itIp ?? '',
    otIp: r.network?.otIp ?? '',
    wifiIp: r.network?.wifiIp ?? '',
    publicIp: r.network?.publicIp ?? '',
    firstSeenMs: Number(r.firstSeenNs / 1_000_000n),
    lastSeenMs: Number(r.lastSeenNs / 1_000_000n),
    lat: r.network?.latitude ?? 0,
    lng: r.network?.longitude ?? 0,
  };
}

/** The caller-org's CLAIMED gateways — the targeting/display resolver. The data
 *  plane is stateless about gateway scope (the server resolves it from the JWT
 *  org), so this exists ONLY so the UI can name/route the gateway it's viewing;
 *  it is NOT a fleet-roster surface (that's a separate org-admin app). Org scope
 *  comes from the verified JWT; the body never carries an org. Paginates fully —
 *  the server caps page_size, so follow next_cursor until empty (bounded). */
export async function listFleet(transport: Transport): Promise<FleetGateway[]> {
  const client = createClient(FleetControl, transport);
  const out: FleetGateway[] = [];
  let cursor = '';
  for (let page = 0; page < 10_000; page++) {
    const reply = await client.listFleet({ pageCursor: cursor, pageSize: 200 });
    for (const g of reply.gateways) out.push(toFleetGateway(g));
    if (!reply.nextCursor) break;
    cursor = reply.nextCursor;
  }
  return out;
}

/** LIVE org fleet — the streaming replacement for the one-shot listFleet(). First
 *  frame is the full snapshot; subsequent ~1 Hz frames carry ONLY the changed
 *  records (FleetUpdate semantics), so we fold them into a roster and yield the
 *  FULL current set each time. Empty `deviceFingerprints` = the whole org roster
 *  (the server scopes by the verified JWT org). This is what makes the gateway
 *  cards' presence LIVE: when a box goes dark the broker flips its leaf-presence
 *  and fleet-control derives it OFFLINE (lastSeen-freshness gated), and that status
 *  delta lands here within ~1s — no reload, no stale "connected". Snapshot-then-
 *  delta scales with what's on screen, not fleet size (the server only watches the
 *  viewport). Roster MEMBERSHIP changes (claim/unclaim) are picked up by RE-opening
 *  the stream — the partial-delta wire carries no removal, so a fresh snapshot is
 *  the authoritative roster; presence/health between those rides the deltas. */
export interface CloudServerLite {
  /** Stable server id. */
  id: string;
  /** Human label — the region/city ("Nuremberg"). */
  name: string;
  /** Coarse geo (city), self-resolved from the box's own public IP. */
  geo: string;
  lat: number;
  lng: number;
}

/** One folded frame of the fleet stream: the full current roster + our private
 *  cloud servers. The server applies access on BOTH: a super-admin receives every
 *  org's gateways and the server list; anyone else receives only their org's boxes
 *  (and servers arrive stat-stripped fleet-side; the map only needs name+geo). */
export interface FleetRoster {
  gateways: FleetGateway[];
  cloudServers: CloudServerLite[];
}

export async function* subscribeFleet(
  transport: Transport,
  deviceFingerprints: string[],
  signal: AbortSignal,
): AsyncGenerator<FleetRoster> {
  const client = createClient(FleetControl, transport);
  const roster = new Map<string, FleetGateway>();
  let cloudServers: CloudServerLite[] = [];
  for await (const upd of client.subscribeFleet({ deviceFingerprints }, { signal })) {
    if (upd.isSnapshot) roster.clear();
    for (const g of upd.gateways) roster.set(g.deviceFingerprint, toFleetGateway(g));
    // `?? []` guards a stale @vitesse/proto (no cloud_servers field) — never crash.
    // Frames replace the full list (same semantics the fleet app relies on).
    if (upd.cloudServers?.length || upd.isSnapshot) {
      cloudServers = (upd.cloudServers ?? []).map((c) => ({ id: c.id, name: c.name, geo: c.geo, lat: c.lat, lng: c.lng }));
    }
    yield { gateways: [...roster.values()], cloudServers };
  }
}

// ── program-indexer: the code-graph index over PLC programs ──────────────────
// The symbol-indexer's sibling: skeleton-first (BlockTree — the block list +
// program hash, never the source), expand-on-demand, and source streamed
// block-by-block from the ObjectStore via NATIVE Connect chunking — the heavy
// ST/XML bytes never sit resident server-side and arrive incrementally client-
// side. The program is relatively STATIC, so ProgramDescriptor.program_hash
// (a combined hash of every block's CompileTime) is the cache key: compare it
// to a persisted copy to know in ONE round-trip whether anything changed.

/** One block in the skeleton (no source — that's GetBlockSource on demand). */
export interface ProgramBlock {
  blockId: string;
  blockType: string; // OB | FB | FC | DB | UDT | …
  blockNumber: number;
  name: string;
  language: string; // STL | LAD | FBD | SCL | …
  compileTime: string; // per-block change key (finer than the program hash)
  stBytes: number; // source size — lets the UI size a progress bar before fetching
  childCount: number; // >0 = expandable grouping node
}

/** The program skeleton: the block list + the descriptor (hash + status). */
export interface ProgramSkeleton {
  controllerId: string;
  programHash: string; // changes iff any block recompiled — the cache key
  status: string; // not_started | extracting | ready | failed
  totalBlocks: number;
  totalStBytes: number;
  blocks: ProgramBlock[];
}

/**
 * Fetch the program SKELETON for a controller — the block list + program hash,
 * NOT the source. BlockTree is a live server-stream (snapshot then recomputes);
 * we take the first frame (the snapshot) and close, exactly like fetchSymbolPage.
 * Cheap and bounded: metadata per block, no source bytes.
 */
export async function fetchProgramSkeleton(transport: Transport, controllerId: string, signal: AbortSignal): Promise<ProgramSkeleton> {
  const client = createClient(ProgramIndex, transport);
  // BlockTree is an INFINITE live stream; we only want its first (snapshot)
  // frame. Drive it on an INTERNAL signal we abort the moment we have that frame
  // (or if the caller aborts) so the Connect stream actually CLOSES — otherwise
  // the api-gateway's program session stays open and never releases the
  // on-demand OBJ mirror it acquired (a slow leak that holds the cloud copy up).
  const ac = new AbortController();
  const onAbort = () => ac.abort();
  signal.addEventListener('abort', onAbort, { once: true });
  try {
    for await (const page of client.blockTree({ controllerId, pageSize: 2000, expanded: [] }, { signal: ac.signal })) {
      return {
        controllerId,
        programHash: page.program?.programHash ?? '',
        status: page.program?.status ?? '',
        totalBlocks: page.program?.totalBlocks ?? page.nodes.length,
        totalStBytes: Number(page.program?.totalStBytes ?? 0n),
        blocks: page.nodes.map((n) => ({
          blockId: n.blockId,
          blockType: n.blockType,
          blockNumber: n.blockNumber,
          name: n.name,
          language: n.language,
          compileTime: n.compileTime,
          stBytes: Number(n.stBytes),
          childCount: n.childCount,
        })),
      };
    }
    throw new Error('block tree: no snapshot frame');
  } finally {
    signal.removeEventListener('abort', onAbort);
    ac.abort(); // close the stream → api-gateway releases the on-demand mirror
  }
}

/** A fetched block's source + its integrity hash (the server's SHA256 of the
 *  full decompressed bytes — verified against what we assembled). */
export interface BlockSource {
  blockId: string;
  text: string;
  bytes: number;
  sha256: string;
}

/**
 * Stream ONE block's source from the ObjectStore via native Connect chunking
 * and assemble it. The bytes arrive incrementally (no whole-blob buffering
 * server-side); we concatenate the chunks and verify the server's trailing
 * sha256. `kind` selects ST (default) / XML / sidecar.
 */
export async function fetchBlockSource(
  transport: Transport,
  controllerId: string,
  blockId: string,
  signal: AbortSignal,
  kind: ProgramBlockKind = ProgramBlockKind.ST,
): Promise<BlockSource> {
  const client = createClient(ProgramIndex, transport);
  const chunks: Uint8Array[] = [];
  let total = 0;
  let sha = '';
  for await (const frame of client.getBlockSource({ controllerId, blockId, kind }, { signal })) {
    if (frame.data.length > 0) {
      chunks.push(frame.data);
      total += frame.data.length;
    }
    if (frame.sha256Hex) sha = frame.sha256Hex; // trailing integrity frame
  }
  const buf = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) {
    buf.set(c, off);
    off += c.length;
  }
  // INTEGRITY CHECK: the source is streamed as sequential chunks with a trailing
  // sha256 of the whole (decompressed) blob. If a chunk was dropped in transit
  // (the api-gateway session mux can drop frames under backpressure), the
  // assembled bytes won't match — surface that as a hard error instead of
  // silently rendering truncated/corrupt source.
  if (sha) {
    const digest = await crypto.subtle.digest('SHA-256', buf);
    const hex = Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    if (hex !== sha) {
      throw new Error(
        `block ${blockId} source integrity check failed (${total} bytes, sha ${hex} ≠ expected ${sha}) — truncated or corrupt stream`,
      );
    }
  }
  return { blockId, text: new TextDecoder().decode(buf), bytes: total, sha256: sha };
}

/** Enrolled-but-unclaimed gateways on the caller's own network ("claim a box
 *  near me"). The network is the cloud-observed source IP, so the request is
 *  empty. Any org member may call it (it's a read); only admins may then Claim. */
export async function discoverNearby(transport: Transport): Promise<FleetGateway[]> {
  const client = createClient(FleetControl, transport);
  const reply = await client.discoverNearby({});
  return reply.gateways.map(toFleetGateway);
}

/** The org's Flows-page description (org resolved from the JWT). '' when unset. */
export async function getOrgDescription(transport: Transport): Promise<string> {
  const reply = await createClient(FleetControl, transport).getOrgDescription({});
  return reply.description;
}

/** Write the org's Flows-page description. '' deletes it. */
export async function putOrgDescription(transport: Transport, description: string): Promise<void> {
  await createClient(FleetControl, transport).putOrgDescription({ description });
}

/** A person asks their org's admins for a permission they were refused — the
 *  client half of <vitesse-gate-overlay>'s Request-access button. The server
 *  routes it to the org's admins as a notification; the caller only needs to
 *  know it was accepted. `context` is where the wall was hit (an entity path),
 *  shown to the admin so the ask is legible. Cloud-scoped like the rest of
 *  FleetControl — the notification lives with the org, not a relay. */
export async function requestAccess(transport: Transport, permissionKey: string, context: string): Promise<void> {
  await createClient(FleetControl, transport).requestAccess({ permissionKey, context });
}

/** Claim an enrolled gateway into the caller's org (ADMIN-only, server-enforced).
 *  The quick path passes claimCode='' — the server proves the claim by LAN-presence
 *  (the caller is on the same network the box enrolled from). A non-empty `claimCode`
 *  is the optional stronger/cross-network proof (the future access-point step).
 *  Returns the claimed record; a non-admin is rejected server-side regardless of the
 *  UI gate. */
export async function claimGateway(
  transport: Transport,
  deviceFingerprint: string,
  claimCode: string,
  displayName: string,
): Promise<FleetGateway> {
  const client = createClient(FleetControl, transport);
  const reply = await client.claim({ deviceFingerprint, claimCode, displayName });
  if (!reply.record) throw new Error('claim returned no record');
  return toFleetGateway(reply.record);
}

// ── dashboards (Dashboards.*) ────────────────────────────────────────────────
/*
 * THE WRITE PATH IS CLIENT-ORCHESTRATED, AND THAT IS THE DESIGN.
 *
 * These calls go from THIS BROWSER to the api-gateway of the gateway in view,
 * which is the only party that can reach every relay: an edge api-gateway is
 * LAN-only and unreachable from Cloudflare by design, so a cloud-side write
 * would work against a cloud-connected box and fail against a LAN-only one —
 * the same call succeeding or failing on where a customer put their relay. A
 * browser on the factory floor reaches the box directly whether or not the box
 * can reach the internet, so composing a board works in both modes here and in
 * exactly one of them anywhere else. This is the same reason installing from
 * the marketplace is client-orchestrated: the catalog never writes org state.
 *
 * If you are adding a server-side dashboard write later, that omission is
 * deliberate rather than unfinished — read the paragraph above before "fixing"
 * it.
 */
/** Roster stream: snapshot then deltas, like every other subscription here. */
export async function* subscribeDashboards(
  transport: Transport,
  gatewayId: string,
  signal: AbortSignal,
): AsyncGenerator<{ upsert?: DashboardSummary; removedId?: string; snapshotComplete: boolean }> {
  const client = createClient(Dashboards, transport);
  for await (const ev of client.subscribe({ gatewayId }, { signal })) {
    if (ev.event.case === 'upsert') {
      const s = ev.event.value;
      yield {
        upsert: {
          id: s.id,
          title: s.title,
          description: s.description ?? '',
          digest: s.digest,
          tileCount: s.tileCount,
          updatedAtMs: Number(s.updatedAtMs),
        },
        snapshotComplete: ev.snapshotComplete,
      };
    } else if (ev.event.case === 'removedId') {
      yield { removedId: ev.event.value, snapshotComplete: ev.snapshotComplete };
    } else {
      yield { snapshotComplete: ev.snapshotComplete };
    }
  }
}

/** One body. Undefined for an id the gateway does not have, which a page shows
 *  as "not found" rather than as an empty canvas. */
/*
 * "MISSING" AND "COULD NOT ASK" ARE DIFFERENT ANSWERS, and collapsing them to
 * one undefined is how a cold deep-link rendered "this dashboard no longer
 * exists" over a board that exists fine — the fetch simply raced the transport
 * coming up, and the page read the failure as an absence. A person believes
 * that message and recreates their board.
 */
export type DashboardBodyResult =
  | { kind: 'ok'; board: DashboardBodyWire }
  | { kind: 'missing' } // the gateway ANSWERED, and the answer is "no such board"
  | { kind: 'unavailable' }; // nobody answered — retry when the connection lands

export async function getDashboard(
  transport: Transport,
  gatewayId: string,
  id: string,
): Promise<DashboardBodyResult> {
  let reply;
  try {
    reply = await createClient(Dashboards, transport).get({ gatewayId, id });
  } catch {
    return { kind: 'unavailable' }; // transport down, gateway unreachable, stream reset
  }
  if (resultFailed(reply.result) || !reply.dashboard) {
    // Only the gateway's own not-found verdict means missing; any other
    // refusal (unavailable, internal) is a failure to ask, not an absence.
    const code = reply.result?.error?.code ?? 0;
    return code === 4 /* CODE_NOT_FOUND */ ? { kind: 'missing' } : { kind: 'unavailable' };
  }
  const d = reply.dashboard;
  return {
    kind: 'ok',
    board: {
      id: d.id,
      title: d.title,
      description: d.description ?? '',
      digest: d.digest,
      updatedAtMs: Number(d.updatedAtMs),
      tiles: d.tiles.map(tileFromWire),
      ...(() => {
        const bg = backgroundFromWire(d.background);
        return bg ? { background: bg } : {};
      })(),
    },
  };
}

/** Create — the METERED call. Refused at the org's ceiling, which is why it is
 *  a separate procedure from Save: an edit at the ceiling must still work. */
export async function createDashboard(
  transport: Transport,
  gatewayId: string,
  board: DashboardBodyWire,
  via = '',
): Promise<{ ok: boolean; error: string; retryable?: boolean; entitlement?: boolean; summary?: DashboardSummary }> {
  /*
   * THE ONE METERED CALL, so the one that can be refused three different ways.
   *
   * The gate rejects before the handler runs, so its refusal is a THROW rather
   * than a Result — and the three cases are not interchangeable to whoever is
   * looking at the screen: a permission sends them to an administrator, a plan
   * ceiling sends them to billing, and a resolver that was briefly unreachable
   * sends them nowhere at all except back in a moment. Collapsing them into one
   * "could not create" is what makes an outage read as a bill.
   */
  let reply;
  try {
    reply = await createClient(Dashboards, transport).create({ gatewayId, via, dashboard: boardToWire(board) });
  } catch (e) {
    const refusal = describeRefusal(e);
    return { ok: false, error: refusal.message, retryable: refusal.retryable, entitlement: refusal.entitlement };
  }
  if (resultFailed(reply.result)) return { ok: false, error: resultErrorText(reply.result) };
  const s = reply.summary;
  return {
    ok: true,
    error: '',
    summary: s && { id: s.id, title: s.title, description: s.description ?? '', digest: s.digest, tileCount: s.tileCount, updatedAtMs: Number(s.updatedAtMs) },
  };
}

/** Save — replace-only, and never metered. `baseDigest` is the digest this
 *  client last saw: a save whose base has moved is REFUSED rather than merged,
 *  because last-write-wins on a board a colleague is also editing loses their
 *  work without telling anyone. */
export async function saveDashboard(
  transport: Transport,
  gatewayId: string,
  board: DashboardBodyWire,
  baseDigest: string,
  via = '',
): Promise<{ ok: boolean; error: string; summary?: DashboardSummary }> {
  const reply = await createClient(Dashboards, transport).save({
    gatewayId,
    via,
    dashboard: boardToWire(board),
    baseDigest,
  });
  if (resultFailed(reply.result)) return { ok: false, error: resultErrorText(reply.result) };
  const s = reply.summary;
  return {
    ok: true,
    error: '',
    summary: s && { id: s.id, title: s.title, description: s.description ?? '', digest: s.digest, tileCount: s.tileCount, updatedAtMs: Number(s.updatedAtMs) },
  };
}

/** The board's change trail, newest first. Empty is a real answer — a board
 *  nobody has edited has nothing to say, which is not a failure. */
export interface DashboardActivityEntry {
  atMs: number;
  actor: string;
  action: string;
  detail: string;
}

export async function dashboardActivity(
  transport: Transport,
  gatewayId: string,
  id: string,
): Promise<DashboardActivityEntry[]> {
  const reply = await createClient(Dashboards, transport).getActivity({ gatewayId, id });
  if (resultFailed(reply.result)) return [];
  return reply.entries.map((e) => ({ atMs: Number(e.atMs), actor: e.actor, action: e.action, detail: e.detail }));
}

export async function deleteDashboard(
  transport: Transport,
  gatewayId: string,
  id: string,
): Promise<{ ok: boolean; error: string }> {
  const reply = await createClient(Dashboards, transport).delete({ gatewayId, id });
  return { ok: !resultFailed(reply.result), error: resultErrorText(reply.result) };
}

/* ─────────────────────────────────────────────────────────── dashboard shares ─
 * The sharer freezes a board; the viewer opens it. Symbol ids cross the wire in
 * subject encoding (what Values.Subscribe and the historian compare against)
 * and come back in the config form the tiles bind, so the two sides of the
 * derivation in share-scope.ts see the same ids.
 */
export interface ShareTermsWire {
  audience: 'link' | 'invite';
  emails: string[];
  expiresAtMs: number;
  allowInteraction: boolean;
}
export interface ShareScopeWire {
  reads: { controllerId: string; symbolId: string }[];
  modelIds: string[];
  commands: { modelId: string; command: string }[];
}
export interface ShareListingWire {
  id: string;
  terms: ShareTermsWire;
  createdBy: string;
  createdAtMs: number;
  revokedAtMs: number;
  recipients: { email: string; revokedAtMs: number }[];
  path: string;
}
export interface OpenShareWire {
  token: string;
  gatewayId: string;
  title: string;
  tiles: DashboardTile[];
  scope: ShareScopeWire;
  expiresAtMs: number;
  allowInteraction: boolean;
}

const audienceToWire = (a: 'link' | 'invite') =>
  a === 'link' ? ShareAudience.LINK : ShareAudience.INVITE;
const audienceFromWire = (a: ShareAudience): 'link' | 'invite' => (a === ShareAudience.LINK ? 'link' : 'invite');
const termsFromWire = (t?: { audience: ShareAudience; emails: string[]; expiresAtMs: bigint; allowInteraction: boolean }): ShareTermsWire => ({
  audience: audienceFromWire(t?.audience ?? ShareAudience.LINK),
  emails: t?.emails ?? [],
  expiresAtMs: Number(t?.expiresAtMs ?? 0n),
  allowInteraction: t?.allowInteraction ?? false,
});

export async function shareDashboard(
  transport: Transport,
  dashboardId: string,
  terms: ShareTermsWire,
  scope: ShareScopeWire,
): Promise<{ ok: boolean; error: string; shareId: string; path: string; linkToken: string }> {
  const reply = await createClient(Dashboards, transport).share({
    dashboardId,
    terms: {
      audience: audienceToWire(terms.audience),
      emails: terms.emails,
      expiresAtMs: BigInt(Math.floor(terms.expiresAtMs)),
      allowInteraction: terms.allowInteraction,
    },
    scope: {
      reads: scope.reads.map((r) => ({ controllerId: r.controllerId, symbolId: encodeSymbolPath(r.symbolId) })),
      modelIds: scope.modelIds,
      commands: scope.commands,
    },
  });
  return { ok: !resultFailed(reply.result), error: resultErrorText(reply.result), shareId: reply.shareId, path: reply.path, linkToken: reply.linkToken };
}

export async function revokeShare(transport: Transport, shareId: string, email = ''): Promise<{ ok: boolean; error: string }> {
  const reply = await createClient(Dashboards, transport).revokeShare({ shareId, email });
  return { ok: !resultFailed(reply.result), error: resultErrorText(reply.result) };
}

export async function listShares(transport: Transport, dashboardId: string): Promise<ShareListingWire[]> {
  const reply = await createClient(Dashboards, transport).listShares({ dashboardId });
  return reply.shares.map((s) => ({
    id: s.id,
    terms: termsFromWire(s.terms),
    createdBy: s.createdBy,
    createdAtMs: Number(s.createdAtMs),
    revokedAtMs: Number(s.revokedAtMs),
    recipients: s.recipients.map((r) => ({ email: r.email, revokedAtMs: Number(r.revokedAtMs) })),
    path: s.path,
  }));
}

export async function openShare(transport: Transport, shareId: string): Promise<{ ok: boolean; error: string; share?: OpenShareWire }> {
  const reply = await createClient(Dashboards, transport).openShare({ shareId });
  if (resultFailed(reply.result)) return { ok: false, error: resultErrorText(reply.result) };
  return {
    ok: true,
    error: '',
    share: {
      token: reply.token,
      gatewayId: reply.gatewayId,
      title: reply.title,
      tiles: reply.tiles.map(tileFromWire),
      scope: {
        reads: (reply.scope?.reads ?? []).map((r) => ({ controllerId: r.controllerId, symbolId: decodeSymbolPath(r.symbolId) })),
        modelIds: reply.scope?.modelIds ?? [],
        commands: (reply.scope?.commands ?? []).map((c) => ({ modelId: c.modelId, command: c.command })),
      },
      expiresAtMs: Number(reply.expiresAtMs),
      allowInteraction: reply.allowInteraction,
    },
  };
}
